/**
 * Raisy blog posts — single source of truth for the public blog.
 * Mirrors playano-docs/raisy/projects/blog/blog01–08 (publish-ready drafts).
 * New post = one entry here; the /blog hub, post pages, and landing section
 * all render from this array (newest-first by date).
 */

import * as React from 'react';
import { Link } from 'react-router';
/* ⚠️ RESTORED THE MISSING IMPORT — the build was broken for every session.
   The blog29 commit rendered `<MermaidDiagram>` in two posts but never imported it, so
   `check_no_broken_names.sh` refused with `TS2304: Cannot find name 'MermaidDiagram'` — and a
   refused gate means NO DEPLOY AT ALL, for anyone. The component has existed at `../MermaidDiagram`
   throughout; only the import line was absent. One line, and the gate is green again.
   Found while trying to ship a pitchdeck change and being unable to. */
import { MermaidDiagram } from '../MermaidDiagram';
import { CostGapChart } from '../sections/visualizations/CostGapChart';
import { RaisyStackChart } from '../sections/visualizations/RaisyStackChart';
import { BotCareerPathChart } from '../sections/visualizations/BotCareerPathChart';
import { AuditTrailChart } from '../sections/visualizations/AuditTrailChart';
import { KernelVersionChart } from '../sections/visualizations/KernelVersionChart';
import { ProcessGraphChart } from '../sections/visualizations/ProcessGraphChart';
import { UrlToCompanyChart } from '../sections/visualizations/UrlToCompanyChart';
import { AutonomyLadderChart } from '../sections/visualizations/AutonomyLadderChart';
import { HallucinationRateChart } from '../sections/visualizations/HallucinationRateChart';
import { ReliabilityGapChart } from '../sections/visualizations/ReliabilityGapChart';
import { MemoryDecayChart } from '../sections/visualizations/MemoryDecayChart';
import { PromptVsMemoryChart } from '../sections/visualizations/PromptVsMemoryChart';
import { InstructionLayerTabs } from '../sections/visualizations/InstructionLayerTabs';
import { LetterOutputTabs } from '../sections/visualizations/LetterOutputTabs';
import { JudgeHeatmapChart } from '../sections/visualizations/JudgeHeatmapChart';
import { OutreachCompetitionMatrix } from '../sections/visualizations/OutreachCompetitionMatrix';
import { OutreachCostStackChart } from '../sections/visualizations/OutreachCostStackChart';
import { OutreachTimelineChart } from '../sections/visualizations/OutreachTimelineChart';
import { OutreachPricingExplorer } from '../sections/visualizations/OutreachPricingExplorer';
import { CompanyFunctionVerdictTable } from '../sections/visualizations/CompanyFunctionVerdictTable';
import { ComparisonTable } from '../sections/visualizations/ComparisonTable';
import { TwoArchitecturesDiagram } from '../sections/visualizations/TwoArchitecturesDiagram';
import { FunnelSankeyChart } from '../sections/visualizations/FunnelSankeyChart';
import { MeetingsPerMeetingCostChart } from '../sections/visualizations/MeetingsPerMeetingCostChart';
import { FundingStagesExplorer } from '../sections/visualizations/FundingStagesExplorer';
import { SpamFilterLayersChart } from '../sections/visualizations/SpamFilterLayersChart';
import { EmailReplyRateDeltaChart } from '../sections/visualizations/EmailReplyRateDeltaChart';
import { EmailFormatVerdictChart } from '../sections/visualizations/EmailFormatVerdictChart';
import {
  PreseedMoneyChart,
  PreseedDealsChart,
  PreseedChequeChart,
} from '../sections/visualizations/PreseedConcentrationCharts';
import {
  WarmVsColdFlowChart,
  WarmVsColdDoorChart,
  WarmVsColdLeverChart,
} from '../sections/visualizations/WarmVsColdCharts';
import {
  DecayOfSpeedChart,
  CloseBySpeedChart,
  WhereRepliesLandChart,
} from '../sections/visualizations/FirstHourCharts';
import { BlogImage } from '@/components/blog/BlogImage';
import { ChartZoom } from '@/components/blog/ChartZoom';
import { WaitlistForm } from '@/components/blog/WaitlistForm';
import { CountdownTimer, LIVE_GATE } from '@/components/blog/CountdownTimer';
import { BottomLine } from '@/components/blog/BottomLine';
import RoundCloseTimer from '../blog/RoundCloseTimer';

/** RULES_BLOG_POST.md Target Audience Taxonomy — required per post, no default. */
export interface BlogPostAudience {
  /** Level 1 — who decides or acts. Raisy operator personas: OpsLead | BotBuilder | Analyst | Admin.
   *  Raisy buyer sub-ICPs (icp_and_phases.md GTM Phase 1): PreSeedFounder | SeriesAFounder |
   *  SeriesBGrowthFounder | FundManagerGP | SPVSyndicateLead. Never tag generic "Founder" —
   *  pick the specific sub-ICP the post's language and proof points actually target. */
  persona: string;
  /** Level 2 — what they already know. Beginner | Intermediate | Professional | Academic | Expert. */
  background: string;
  /** Level 3 — how deep the post goes. Overview | HowTo | Applied | DeepDive | PeerReview. */
  depth: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  category: string;
  series: string;
  /** Required, not optional — a post with no declared audience cannot compile (RULES_BLOG_POST.md, audience tag rules). */
  audience: BlogPostAudience;
  excerpt: string;
  keywords: string[];
  image: string;
  content: React.ReactNode;
  /** FAQ block rendered by the post page with FAQPage JSON-LD. */
  faq?: { q: string; a: string }[];
  /** Sources footer rendered by the post page. */
  sources?: string;
}

/** Internal-link helper — /blog hub link used inside post bodies. */
export const BlogHubLink = ({ children }: { children: React.ReactNode }) => (
  <Link to="/blog" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
    {children}
  </Link>
);

/**
 * RENAMED SLUGS (2026-09-15). Two published slugs carried a word the communication rules forbid in a
 * user-facing string (`RULE_communication_rules_100.md` Rule #102: `problem`, `gap`). A URL is
 * user-facing, so both were renamed; these redirects keep every inbound link and search result
 * working rather than returning a 404.
 */
export const SLUG_REDIRECTS: Record<string, string> = {
  'the-investor-outreach-execution-gap': 'the-investor-outreach-execution-challenge',
  'the-ai-reliability-problem-in-numbers': 'the-ai-reliability-challenge-in-numbers',
};

export const BLOG_POSTS: BlogPost[] = [
  // Architecture comparison against an externally published multi-agent design (J.P. Morgan Private
  // Bank, "Ask David", LangChain Interrupt 2025, shared with us and verified 2026-10-07). The
  // argument is STRUCTURAL, not a capability contest: a supervisor agent answers questions for a
  // human who then acts, while process rooms behind a gate at the external effect ARE the acting.
  // Every claim about the compared system is taken from its own talk, slides and published
  // write-ups, each named in `sources` below. Nothing was measured, and the post says so in its own
  // "What this post does not answer" section. The two diagrams are Mermaid text rendered by the
  // MermaidDiagram component the public site already uses, wrapped in ChartZoom.
  {
    slug: 'multi-agent-supervisor-architecture',
    title: 'Multi-Agent Supervisor Architecture, Compared',
    date: '2026-10-07',
    category: 'Vision',
    series: 'The Raisy Blog',
    audience: { persona: 'BotBuilder', background: 'Professional', depth: 'Applied' },
    image: '/blog/multi-agent-supervisor-architecture.jpg',
    excerpt:
      'J.P. Morgan runs one supervisor over three sub-agents. Raisy runs a Chief of Staff over process rooms that own state. Where the two designs diverge.',
    keywords: [
      'multi-agent supervisor architecture',
      'supervisor agent pattern',
      'multi-agent AI architecture',
      'AI agent approval gate',
      'architecture comparison multi-agent',
    ],
    content: (
      <>
        <p>
          <strong>
            Two multi-agent systems can both be right and still not be substitutable, because the
            difference that matters is not how many agents each one has. It is where the human stands.
          </strong>
        </p>

        <h2>Two systems, opposite ends of one pipeline</h2>
        <p>
          In May 2025, two engineers from J.P. Morgan Chase Private Bank gave a talk at LangChain
          Interrupt about <em>Ask David</em>, an investment research assistant their team built for a
          private bank. The name is an acronym: Data, Analytics, Visualization, Insights,
          Decision-making. One supervisor agent sits above three specialists. One turns a question
          into SQL or GraphQL, one retrieves document chunks from a vector index, one calls
          proprietary models for fund performance, attribution and exposure. A person reviews the
          answer.
        </p>
        <p>
          We built the other arrangement. In Raisy the unit is not a question, it is a{' '}
          <strong>process</strong>, and a process room is a real room with its own state, its own
          queue and its own measures. The room&apos;s caretaker is a{' '}
          <strong>process harness</strong>: it holds the state, runs the room&apos;s staff, enforces
          the budget and the tool allowlist, and reports upward. Above the rooms sits the{' '}
          <strong>Chief of Staff Room</strong>, whose actor coordinates and never performs. The user
          sits in the <strong>boardroom</strong> and may walk into any room at any time.
        </p>
        <p>
          The identifying feature of our arrangement is one sentence: the human is not the last mile.
          The human is the gate from the first mile, and the machine&apos;s job is to make the gate
          cheap and then remove itself from it by evidence.
        </p>

        <ChartZoom>
          <MermaidDiagram
            id="blog29-two-arrangements"
            diagram={`flowchart TB
    subgraph THEIRS["THEIRS · J.P. MORGAN PRIVATE BANK — the “Ask David” research agent"]
        direction TB
        QT("an advisor's question") --> SUPT(["SUPERVISOR AGENT<br/>plan · reflect · engage · memory"])
        SUPT --> T1("structured data specialist")
        SUPT --> T2("unstructured data specialist<br/>vector index")
        SUPT --> T3("analytics specialist")
        T1 --> ANST("a curated answer, a formatted report, a chart")
        T2 --> ANST
        T3 --> ANST
        ANST --> HT{"a person reads it, then acts"}
    end
    subgraph OURS["OURS · RAISY — the owner, his rooms, and the gate at the effect"]
        direction TB
        UO(["THE BOARDROOM<br/>objective: the owner's seat — where his decision is due"]) --> COSO(["THE CHIEF OF STAFF ROOM<br/>objective: supervise · figure it out first · curate attention"])
        COSO --> R1("PROCESS ROOM A<br/>objective: run one process to completion")
        COSO --> R2("PROCESS ROOM B<br/>objective: run one process to completion")
        R1 --> GO{"THE GATE<br/>human review at the external effect"}
        R2 --> GO
        GO --> OUTO("the act, beyond the entity wall")
        UO -.->|"may enter any room"| R1
    end
    class QT,SUPT,T1,T2,T3,ANST theirs
    class COSO,R1,R2 ours
    class HT,UO human
    class GO gate
    class OUTO act`}
          />
        </ChartZoom>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center -mt-4 mb-8">
          <strong>Teal marks ours, grey marks theirs, and blue marks the person on both sides</strong> —
          which is the honest way to draw it, because both designs put a human in the loop. The
          difference is where. On theirs the person reads a finished answer and then acts, off the
          diagram. On ours the person <em>is</em> the gate, and the act does not happen until he
          says so.
        </p>

        <h2>The last mile, and the gate</h2>
        <p>
          The bank&apos;s third lesson names its mechanism plainly: <em>Human SME in the loop</em> is
          the <strong>last mile</strong> of an accuracy ladder, after prompt engineering, chunking
          strategy, search methodology and workflow refinements have taken the system from under 50
          percent to 80 and then 90 percent. The slide says of that last mile: &ldquo;Hardest. May be
          not achievable.&rdquo;
        </p>
        <p>
          That is an honest sentence. The human is what remains when engineering runs out of road. It
          is also not the only place that system uses a person: for complex analytics queries it
          generates code and supervises the execution, which puts a human in front of the consequence
          rather than after it. And in May 2026 the same team published a second version that reworked
          the supervisor into a Deep Agent driven by the bank&apos;s proprietary private-banking{' '}
          <strong>Skills</strong>, described as the business&apos;s encoded knowledge of how its
          research work is actually done. That last move is worth remembering, because it is the
          direction we went in first: a kernel is exactly that, encoded once and versioned.
        </p>

        <h2>Four mechanisms carry the difference</h2>
        <ul>
          <li>
            <strong>A room holds state, never memory.</strong> One company has one boardroom and one
            process room per process. A room owns its status, its queue items and its measures. It
            does not own knowledge: knowledge lives in the single entity memory, visible from the
            boardroom always. A stale roll-up is treated as a loud failure, not as an absence of news.
          </li>
          <li>
            <strong>Recall is exact, not similar.</strong> Their unstructured agent is a retrieval
            agent over a vector database and a hierarchical index, which is the correct design when
            the corpus belongs to someone else. Ours is a lookup: a fact is recalled by entity and by
            dimension, newest version first. Vector search has no runtime role in the memory path. The
            reason is not that similarity search is bad, it is that it puts variance into what the
            model sees, and a governed record cannot have a retrieval lottery in it.
          </li>
          <li>
            <strong>The asking engine, not a better prompt.</strong> Their second lesson is
            evaluation-driven development, and it is a good answer to a real problem. We made the
            question itself a governed component: never write a field that was not read from a named
            source, verify in a declared order before asking, never ask twice, and treat an uncited
            field as a schema violation rather than a guess. The question count is measured as the
            user&apos;s cost.
          </li>
          <li>
            <strong>A budget for proactivity.</strong> A system that answers is a tool the customer
            drives. Raisy initiates, on a clock, on an event or on a measure that moved, inside a
            budget whose currency is one decision waiting on the owner. Proactive work may never
            consume the capacity a human review needs.
          </li>
        </ul>

        <h2>The house behind the gate</h2>
        <p>
          Our arrangement, in the same picture language: the reporting line runs from a process
          harness up to the Chief of Staff and then to the boardroom, and the user&apos;s path into
          any room is never blocked.
        </p>
        <p>The house has three spaces and each has one short objective; the objective is the test to
          hold a space to, so a space that stops serving its objective is the thing that is wrong.
          A fourth thing stands outside it, and it is not a room of this house at all.</p>
        <ul>
          <li>
            <strong>The boardroom — the owner&apos;s seat.</strong> <em>Objective: be the one place
            where his decision is due.</em> He is the only approver, and this is where prepared items
            wait, each with its evidence attached. He may walk into any room at any time, unrestricted,
            which is a right rather than a feature: the reporting line is a convenience, never a wall.
          </li>
          <li>
            <strong>The Chief of Staff Room — the reporting line, never a gate.</strong>{' '}
            <em>Objective: supervise the rooms, figure it out before escalating, and curate what
            deserves the owner&apos;s minutes.</em> It coordinates and never performs, so it holds no
            work of its own. Items do not flow through it; state roll-ups do. That is what keeps it
            from becoming the bottleneck it looks like it should be.
          </li>
          <li>
            <strong>A process room — one process, one room.</strong> <em>Objective: run that process
            to completion.</em> It holds the process&apos;s state, its queue and its measures. A room
            is minted when a process exists, never for an org chart. Its caretaker is a process
            harness that runs the room&apos;s bots, enforces a budget and a tool allowlist, and reports
            upward. The room holds state; it does not own knowledge.
          </li>
          <li>
            <strong>The gatehouse — a separate house, not a room of this one.</strong>{' '}
            <em>Objective: hold the only crossing of the entity wall.</em> It is deliberately
            <em>not</em> in the ladder above, and not a process room of its own: it is a peer of the
            whole house, with its own harness, its own failure domain and its own measures. It does
            not wait for a process to exist — it stands with the entity, as a wall, from the first
            day, and it carries two gates rather than one: what comes in, and what goes out. That
            separation is the point. If the thing that decides what may leave the company were a room
            inside the company, the same agent that wants to act would also be the one holding the
            door.
          </li>
        </ul>

        <ChartZoom>
          <MermaidDiagram
            id="blog29-the-house"
            diagram={`flowchart TB
    subgraph HOUSE["OURS · RAISY — THE HOUSE · board room → Chief of Staff room → process rooms"]
        direction TB
        U(["THE OWNER<br/>internal · the only approver"]) -->|"his default door"| BR["THE BOARDROOM<br/>objective: the place his decision is due"]
        BR --> COS["THE CHIEF OF STAFF ROOM<br/>objective: supervise the rooms, figure it out before escalating, curate his attention"]
        COS -->|"roll-up + receipt"| BR
        COS --> PR1["PROCESS ROOM A<br/>objective: run one process to completion"]
        COS --> PR2["PROCESS ROOM B<br/>objective: run one process to completion"]
        COS --> PR3["PROCESS ROOM N<br/>minted when a process exists, never for an org chart"]
        PR1 -->|"state roll-up (stale = LOUD)"| COS
        PR2 -->|"state roll-up (stale = LOUD)"| COS
        PR3 -->|"state roll-up (stale = LOUD)"| COS
        U -.->|"may walk into ANY room, unrestricted"| PR1
    end
    subgraph SEP["A SEPARATE HOUSE, NOT A ROOM OF THE ONE ABOVE — the entity wall"]
        direction TB
        GH["THE GATEHOUSE<br/>objective: hold the only crossing of the entity wall<br/>two gates: entry + exit · its own harness · its own KPIs"]
    end
    PR1 -.->|"the only crossing, by hand, in the gated format"| GH
    class U human
    class BR,COS,PR1,PR2,PR3 ours
    class GH gate`}
          />
        </ChartZoom>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center -mt-4 mb-8">
          The Chief of Staff is the reporting line, never a wall. A room that stops reporting is an
          incident, not an absence of news.
        </p>

        <h2>What the user actually sees</h2>
        <p>
          On their side, an advisor asks a question and gets a curated answer, a formatted document or
          a chart, and a subject-matter expert has reviewed it. On ours, the user opens the boardroom
          and finds prepared items waiting on a decision, not an empty box waiting for a question.
          Each item carries its citations and can be approved, edited or rejected. Four classes reach
          the user and nothing else does: an external effect that needs authorising, a judgement the
          machine could not clear, something irreversible it cannot classify, and a loud failure with
          the proposed next step attached. A person who has not looked at the system for five days
          returns to a digest, not a backlog.
        </p>

        <h2>Which one you should build</h2>
        <p>
          The honest verdict is that these are different products, so the useful question is which one
          your situation is. The table below is here so a reader who reads nothing else still gets an
          answer.
        </p>
        <div className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-300 dark:border-slate-600">
                <th className="text-left p-2 font-semibold">If this is your situation</th>
                <th className="text-left p-2 font-semibold">Build</th>
                <th className="text-left p-2 font-semibold">Why</th>
                <th className="text-left p-2 font-semibold">Confidence</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <td className="p-2">Answers, reports or charts from a document corpus you do not own</td>
                <td className="p-2">A supervisor over retrieval specialists</td>
                <td className="p-2">The corpus is the product, and a similarity index is a defensible fit for documents no one governs</td>
                <td className="p-2">High</td>
              </tr>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <td className="p-2">A function that acts, with an external effect, for an owner</td>
                <td className="p-2">Process rooms behind a gate at the effect</td>
                <td className="p-2">The artefact is a state change, so the gate belongs where the consequence crosses the boundary</td>
                <td className="p-2">High</td>
              </tr>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <td className="p-2">You cannot name your specialist agents before reading the company</td>
                <td className="p-2">Grow a room per process</td>
                <td className="p-2">A fixed agent taxonomy forces a design decision about a company you have not looked at yet</td>
                <td className="p-2">High</td>
              </tr>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <td className="p-2">The same question must produce the same context twice</td>
                <td className="p-2">Exact, keyed recall</td>
                <td className="p-2">A similarity search varies what the model sees, which makes a wrong answer hard to attribute</td>
                <td className="p-2">Medium</td>
              </tr>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <td className="p-2">Autonomy must increase over time without a policy rewrite</td>
                <td className="p-2">Earn it per unit, with the gate as an owner setting</td>
                <td className="p-2">An earned threshold is measurable and reversible; a binary switch is neither</td>
                <td className="p-2">High</td>
              </tr>
              <tr>
                <td className="p-2">You are regulated and keep a named expert in the loop by policy</td>
                <td className="p-2">Keep the expert, and call it a policy choice</td>
                <td className="p-2">Their own slide says the last mile may not be achievable, which may be governance rather than capability</td>
                <td className="p-2">Medium</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Where their design is the better answer</h2>
        <p>
          A comparison that only points one way is not a comparison. Their retrieval design is right
          for a corpus no single company governs, and it is what most teams should build first. Their
          independent evaluation of sub-agents, testing each specialist separately from the whole
          flow, is a practice worth copying and it is stated more clearly in their slides than in our
          own rules. Their metrics per agent type, including faithfulness and response relevance for a
          retrieval agent, are the right shape of measure for a research assistant. Our own
          position is narrower than a first reading suggests. We do measure the agent, but what we
          measure is its trustworthiness: one confidence score per bot, approvals divided by runs,
          and the rule that defines it separates the two instruments in as many words. We do not
          yet measure the agent&apos;s quality, and the reason is not a missing idea. A bot KPI is
          already defined as what a bot produces, output accuracy, throughput and latency. It was
          decided in August and its enforcement half was never built, which is a different failure
          from not knowing what to build. The domain itself,
          fund attribution and exposure, is substantive finance rather than retrieval plumbing.
        </p>
        <p>
          They also put reflection in the flow as a first-class node, which is easy to point at. Ours
          is distributed across the Chief of Staff&apos;s duty to complete the evidence chain before
          escalating, the asking engine&apos;s declared order of checks, and the rule that a stale
          roll-up is loud. Distributed is more robust and harder to draw, and that is a fair
          criticism of how our design reads.
        </p>

        <h2>The gate is where the field is still improvising</h2>
        <p>
          One talk is not a field. The same conference and the same published collection of lessons
          include four other production systems, and together they say something more useful than any
          single comparison. BlackRock presented LangGraph agents with a plugin registry that lets
          more than fifty engineering teams contribute tools and agents, evaluated in daily CI;
          Bridgewater split its analyst tool into a chat agent and a deterministic coding agent,
          arguing that correctness is easier to guarantee when coding is treated as a compiler
          problem, and instances one agent per user scoped to that user&apos;s entitlements; Chime
          built its member-facing agent with legal partners co-writing the evaluations. Morgan Stanley
          reports that 98 percent of its Financial Advisor teams have adopted its assistant, which is
          the strongest adoption number in the set and a reminder that adoption and governance are
          different problems.
        </p>
        <p>
          The pattern is that everyone has converged on the same agent shapes, and almost nobody has
          converged on where the human stands. J.P. Morgan&apos;s own written guidance puts it as a
          principle rather than a feature: governance and compliance move into runtime, with spend
          limits, approval checkpoints and redaction treated as runtime concerns rather than
          review-time paperwork. We agree with all of it, and we took it one step further. If
          governance belongs in runtime, then the gate is not a screen bolted onto a finished
          pipeline. It is the reason the pipeline has the shape it has.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Every serious agent deployment eventually meets the same question: what does the system do
          when nobody is watching it. The two architectures answer it in opposite ways. One keeps
          raising accuracy until a person can stop checking, and where that is impossible, keeps the
          person. The other treats the person as a permanent gate and treats the machine&apos;s
          progress as the gate getting quieter: an approval rate that climbs, an override rate that
          falls, a rung of autonomy earned per unit of work. Both can be honest. Only one of them is a
          claim about the owner&apos;s time.
        </p>
        <p>
          That is the promise worth holding the system to: AI prepares, humans decide, trust is
          earned. A gate that never opens is a bottleneck with good manners. A gate with no evidence
          behind it is a liability. What makes the arrangement work is that every crossing is
          recorded, every authorisation is attributable, and the width of the gate is set by the owner
          rather than by the builder.
        </p>

        <p>
          <Link
            to="/entities"
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Open your company at raisy.team/entities
          </Link>{' '}
          to see what a run company looks like from the owner&apos;s chair.
        </p>
      </>
    ),
    faq: [
      {
        q: 'Is a supervisor agent the same thing as an orchestrator?',
        a: 'A supervisor agent is an orchestrator that also decides. It receives the question, plans the route, chooses which specialist to engage and reflects on the result. An orchestrator that only routes adds a hop. The distinction matters because the supervisor is where the judgement lives, and in a governed system judgement is the thing that has to be attributable.',
      },
      {
        q: 'Why does Raisy not use vector search for memory?',
        a: 'Because memory is a governed record, and a similarity search would vary what the model sees between two identical questions. Facts are recalled by entity and by dimension, newest version first, which is a lookup rather than a search. Vector search is still permitted at design time, where a choice is made once.',
      },
      {
        q: 'What is a process room, in one paragraph?',
        a: 'A process room is a real room inside one company, one per process, holding that process\u2019s state, its queue and its measures. Its caretaker is a process harness which holds state, runs the process\u2019s bots, enforces a budget and a tool allowlist, and reports upward. The room does not own knowledge; knowledge is shared across the company.',
      },
      {
        q: 'How does the gate open without a human reading everything?',
        a: 'By evidence. Each unit of work carries a record of approvals and overrides, and the width of the gate is a setting the owner holds: review every run, review before anything external, or let anything above a stated confidence pass. The approval mode resolves from the narrowest level outward, so a step can be stricter than the process it belongs to.',
      },
      {
        q: 'Do the two systems compete?',
        a: 'They can, and they can also be layered. A research assistant is a good consumer of answers; an operating system is a good place to put the gate. The mistake is to buy the first while needing the second, then discover the missing piece is not another agent but a boundary where a consequence can be authorised.',
      },
      {
        q: 'What does "stale roll-up is a loud failure" mean in practice?',
        a: 'It means a room that stops reporting is treated as an incident, not as an absence of news. If a room\u2019s state is stale and no one can see it, the room above it is blind, which is the exact failure that shared visibility exists to prevent.',
      },
      {
        q: 'Does Ask David publish accuracy or adoption numbers?',
        a: 'It publishes an accuracy curve, under 50 percent to roughly 80 and then 90 percent, with the last mile left to a human reviewer, and a scope statement counting thousands of investment products. It does not publish user counts or production usage metrics. The large numbers sometimes attributed to it, 200,000 users in eight months and 250,000 employees with access, belong to that firm\u2019s separate internal assistant, which is a different system.',
      },
      {
        q: 'Is a Skill in their second version the same thing as a kernel in Raisy?',
        a: 'They solve the same problem from opposite ends. A kernel is the unit we version: a piece of prompt or knowledge with an identity, an owner and a history, assembled into bots and processes. Their Skills are the same instinct, the bank\u2019s encoded knowledge of how its research work is done, attached to a supervisor that became a Deep Agent in 2026. The difference is what surrounds the unit: ours sits inside a house with rooms that hold state and a gate at the external effect.',
      },
    ],
    sources:
      'J.P. Morgan Chase Private Bank, "How JP Morgan Built An AI Agent for Investment Research with LangGraph", LangChain Interrupt 2025, published 29 May 2025 (presenters David Odomirok and Zheng Xue): the architecture, the three lessons and the accuracy ladder. · The same team\'s architecture slides, shared with us and read 7 October 2026: The Approach, the two sub-agent descriptions, Graph: Workflow, and Lessons 1 to 3. · ZenML LLMOps Database, "Multi-agent investment research assistant with RAG and human in the loop", read first-hand 7 October 2026: the DAVID acronym, MongoDB as a storage layer, the text-to-code execution gate, the personalization, reflection and summarization nodes, and the development order. · Farhan Baluch, "Inside JPMorgan\'s Investment Research Agent Architecture", 30 May 2025, read first-hand the same day: reproduces the diagram and the three lessons. · JPMorganChase, "LLM Suite wins American Banker\'s Innovation of the Year Grand Prize", read first-hand: 200,000 onboarded users in eight months, a different system from the research assistant. · CNBC, 30 September 2025, read first-hand: 250,000 employees with access and roughly half using it daily, also a different system. · BlackRock, "How BlackRock Built Production AI Agents for Asset Management with LangGraph", LangChain Interrupt 2025, published 12 June 2025: URL verified, and the operational detail in the peer-set section comes from secondary write-ups of the same event, which this post discloses in "What this post does not answer". · Raisy\'s own architecture of record, cited by rule and decision ID rather than restated: the manifest rules on the human gate, runtime recall, the isolation boundary, the governed substrate and proactivity, and the project files carrying the Chief of Staff doctrine, the asking engine and the proactivity budget. Nothing in this post is a measurement of ours and no endpoint call backs a number in it.',
  },
  // Category comparison against OpenAI's dots (announced 29 September 2026). The argument is
  // STRUCTURAL, not a capability contest: a dot belongs to a person's account, a company function
  // belongs to the company, and memory, permission, audit, the measure and handover all follow from
  // that. Every claim about the other products is published information, named in `sources` below.
  // Nothing was measured, and the post says so in its own "What this post does not answer" section,
  // which is why no empirical figure appears here (RULES_BLOG_POST no-invented-data rule).
  {
    slug: 'openai-dots-alternative',
    title: 'An OpenAI Dots Alternative for Companies',
    date: '2026-10-01',
    category: 'Vision',
    series: 'The Raisy Blog',
    audience: { persona: 'SeriesAFounder', background: 'Professional', depth: 'Applied' },
    image: '/blog/openai-dots-alternative.jpg',
    excerpt:
      'Dots gives one person their own computer. A company needs one owner for a function, a gate before anything leaves, and a number somebody watches.',
    keywords: [
      'OpenAI dots alternative',
      'ChatGPT dots for business',
      'always-on AI agent for a company',
      'personal AI agent vs company function',
      'AI agent governance',
    ],
    content: (
      <>
        <p>
          <strong>
            There is a function in your company that runs on your own attention: the raise, the pipeline, the
            follow-up. It needs one owner, a gate before anything leaves, and a number you chose. A personal
            assistant gives you one of those three.
          </strong>
        </p>

        <h2>What changed on 29 September</h2>
        <p>
          OpenAI announced dots at DevDay on 29 September 2026: always-on agents that live inside ChatGPT, each with
          its own cloud computer and browser, connected to more than 4,000 apps through plugins, able to keep
          working between conversations. The first dot is included with ChatGPT Pro and Business Premium. A preview
          of specialist dots, each with its own identity and credentials, runs in enterprise pilots where OpenAI
          engineering works directly with the organisation.
        </p>
        <p>
          That is a large release, and it answers a question a lot of people have been asking for a year: what does
          an assistant look like when it does not stop when you close the tab. If you are one person with your own
          tasks, it is a reasonable answer, and it arrives inside a subscription you may already pay for.
        </p>
        <p>
          If you are a company running a function, the question you are actually asking is different. Not whether it
          can do the work, but who owns it, what it answers for, and what happens on the day it touches a customer.
        </p>

        <h2>What the difference actually is</h2>
        <p>
          Most dots belong to one person's ChatGPT account, so their memory, their permissions and their reach end
          there. Their company tier exists and it is worth naming properly, because it is the honest counter to any
          claim that this is only a personal tool: a specialist dot has its own identity and credentials, and it runs
          in enterprise pilots where their engineers work with you directly. So the deciding difference is not who
          owns the account.
        </p>
        <ul>
          <li>
            <strong>Memory.</strong> A dot remembers for one person. A company's function remembers for the company,
            so it survives a colleague leaving and does not have to be retaught by whoever is on shift.
          </li>
          <li>
            <strong>Permission.</strong> A dot's reach is one person's access to their own apps. A company's function
            is scoped to the company's workspace, with per-person roles inside it.
          </li>
          <li>
            <strong>Audit.</strong> A company that must show an auditor what its agents did needs a record per
            action, per company, not a transcript inside somebody's chat history.
          </li>
          <li>
            <strong>The measure.</strong> A company watches a number: meetings agreed, replies received, a round
            closed. A personal assistant has no such number to be judged against.
          </li>
          <li>
            <strong>Handover.</strong> Work owned by the company can be reviewed by whoever is available that week.
            Work owned by one person's account does not survive that.
          </li>
        </ul>
        <p>
          Two posts go deeper on the mechanics this implies.{' '}
          <Link
            to="/blog/humans-decide-bots-prepare"
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Humans decide, bots prepare
          </Link>{' '}
          sets out why the gate exists at all, and{' '}
          <Link
            to="/blog/every-action-leaves-a-receipt"
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Every action leaves a receipt
          </Link>{' '}
          shows what the gate writes down.
        </p>

        <h2>The two shapes, side by side</h2>
        <ChartZoom>
          <TwoArchitecturesDiagram />
        </ChartZoom>

        <h2>How a company function is built</h2>
        <p>
          Five things make a function ownable at company level rather than by a person, and each one is a property of
          the workspace, not of the model.
        </p>
        <ol>
          <li>
            <strong>The rules belong to the company and are versioned.</strong> A rule the function follows is a
            numbered, dated version. Editing it creates a new version and leaves the old one readable, so "why did
            it say that in March" has an answer.
          </li>
          <li>
            <strong>Every call and every run writes a row.</strong> One row per invocation: what ran, with which
            rules, on whose company, with what result.
          </li>
          <li>
            <strong>A process is a graph, and a gate is a node in it.</strong> A repeatable method is written as
            steps executed in order, with approval gates that stop the run before anything crosses to the outside
            world.
          </li>
          <li>
            <strong>The measure is per company, and it traces to an objective.</strong> A number is only useful if
            you can say which goal it serves, so each measure points back at the one it belongs to.
          </li>
          <li>
            <strong>Access is scoped.</strong> Owner, editor and viewer, per company. The workspace is the boundary,
            so isolation between two companies is not a promise about behaviour, it is the shape of the data.
          </li>
        </ol>

        <h2>What the user actually sees</h2>
        <p>
          One review queue, and a count that moves only when they decide. Opening the workspace, reading an item, or
          switching between companies changes nothing. Approving or rejecting something is what moves the number,
          which is the only reason the number is worth following. Nobody hunts through five tools to find out what
          needs them, and nobody is asked to approve something whose evidence they have not seen.
        </p>

        <h2>Where dots is the better answer</h2>
        <p>
          It is worth being exact about this, because a comparison that recommends us everywhere is not a
          comparison. If you are one person, working on your own material, and you already pay for one of the plans
          that includes a dot, then the dot is the right tool and it costs you nothing extra to try. It is built for
          that situation, it comes with the subscription, and it connects to more apps than we do. We are not trying
          to win that case, and you should not let anyone talk you out of it.
        </p>
        <p>
          The case where the shape decides is a company that has to hand a function a number, and answer for it
          afterwards. That is the case we build for.
        </p>

        <h2>So which one do you need</h2>
        <ChartZoom>
          <CompanyFunctionVerdictTable />
        </ChartZoom>

        <h2>What each one actually gives you, row by row</h2>
        <p>
          Read this as a buyer and not as a supporter of either product. Every row about theirs is published
          information, named in the sources. Every row about ours is something that exists in our own system.
        </p>
        <ChartZoom>
          <ComparisonTable />
        </ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          The interesting question about always-on agents is not how much they can do. It is what happens on the day
          one of them is wrong in front of a customer, and whether you can find out why.
        </p>
        <p>
          A company that can answer three questions is fine. Which function is this? Who approved it going out? What
          number was it supposed to move? A personal agent that lives in one person's account, with that person's
          apps connected and a memory that leaves with them, makes all three hard to answer, not because the product
          is careless but because the account it belongs to is the wrong shape for the question.
        </p>
        <p>
          That is the whole of our position. We are not a better personal assistant. We are the thing a company
          points at when it says: this is who runs it, this is the gate, and this is the number.
        </p>

        <h2>What this post does not answer</h2>
        <ol>
          <li>
            <strong>We have not measured either product against the other.</strong> There is no head-to-head task
            run behind this post, no judged arm, no score. Every fact about the other products here is published
            information listed in the sources, and every fact about us is a published capability or a published
            price.
          </li>
          <li>
            <strong>We make no cost comparison.</strong> OpenAI has not published per-seat pricing for team plans or
            for extra dots, so any total cost claim from us would rest on a number we invented. The honest version
            is that a company plan does not grow with headcount, and you can check the rest yourself.
          </li>
          <li>
            <strong>The map is a structural reading, not a measurement.</strong> It places each product on two
            defined axes. The axes are definitions we choose to draw, and the placement follows from the published
            shape of each product. It is not a score.
          </li>
          <li>
            <strong>The decision itself is yours.</strong> Nothing here tells you whether your company should run a
            function with a person, a personal agent, or a governed process. It tells you what the difference
            between those answers is, so that the decision is made for a reason.
          </li>
        </ol>

        <p>
          If the shape question is settled and the raise is the function you want to run first,{' '}
          <Link to="/entities" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            open your company at raisy.team/entities
          </Link>{' '}
          and let the first function run with a gate on it.
        </p>

        <p>
          For the vocabulary we use for rounds, see{' '}
          <Link
            to="/blog/startup-funding-stages-explained"
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            startup funding stages, explained
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink>.
        </p>
      </>
    ),
    faq: [
      {
        q: 'Is Raisy an alternative to ChatGPT dots?',
        a: 'For a company running a function, yes. For one person with their own tasks, no. Dots is built around one person\'s account and Raisy is built around one company\'s workspace.',
      },
      {
        q: 'Can I use both?',
        a: 'Yes, and most companies will. A dot is a personal tool and a company-level function is an operating one. Nothing about using a personal assistant stops a company from running a function properly.',
      },
      {
        q: 'Does Raisy replace ChatGPT?',
        a: 'No. The underlying model is a layer we route per call rather than the thing we sell. What we add is the part around it: whose rules, whose memory, whose permissions, which gate, and which number.',
      },
      {
        q: 'Does ChatGPT dots have an alternative for individuals?',
        a: 'Not from us. We have no personal plan and no consumer product. Every tier we publish is priced per company.',
      },
      {
        q: 'How is Raisy priced?',
        a: 'Per company, by monthly outreach volume, not per seat. The three paid tiers are 49, 149 and 399 euros a month, and the free tier includes five reachouts. Ten people on one company plan cost the same as two.',
      },
      {
        q: 'What if I only need a small function?',
        a: 'Start on the free tier with five reachouts and one function. If the function does not earn its place you have spent nothing, and the workspace deletes cleanly.',
      },
    ],
    sources:
      'OpenAI, "Introducing dots", product announcement, 29 September 2026 (openai.com/index/introducing-dots): launch scope, own cloud computer and browser, more than 4,000 connected apps, plan eligibility for Pro and Business Premium, background work with read-only tools, custom rules and their four behaviours, the safeguards that stay with the account holder, the desktop-only setup, the launch limits including one primary dot per person, and the specialist-dots enterprise pilot. · OpenAI Help Center, "Getting started with your dot" (article 20001530): plan and region eligibility, the age floor of 18, and the region exclusion for Pro access in the EEA, Switzerland and the United Kingdom. · Launch-day coverage from named outlets, used only for facts OpenAI\'s own pages do not state: the region exclusion as reported, the desktop-only creation flow, the absence of published benchmarks or independent evaluations, and that a dot does not place calls and does not hold an email address of its own. Each claim is attributed to the outlet that reported it where it appears. · Raisy\'s own published plan prices, monthly outreach volumes and capability descriptions, which are published and not measured here. Live verification against a public Raisy endpoint is pending that endpoint being public, and no number in this post depends on it.',
  },
  // The CLOSED BETA announcement — the successor to `the-raisy-waitlist-is-open` (the alpha
  // announcement, 2026-08-25). Same door, same signup: the one waitlist form writing to the one
  // Google Sheet, so a beta signup lands beside the alpha ones. The alpha post keeps its own
  // (closed) countdown; this one carries the live one, and the sticky banner follows it.
  // To move the date, change BETA_CLOSES_AT in `components/blog/CountdownTimer.tsx` — the post,
  // the countdown and the banner all read that single line.
  {
    slug: 'the-raisy-beta-is-open',
    title: 'Raisy Closed Beta Is Open: Fundraise and Sell at Once',
    date: '2026-09-18',
    category: 'Announcement',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Beginner', depth: 'Overview' },
    image: '/blog/the-raisy-beta-is-open.jpg',
    excerpt:
      'Raising takes months you should be spending on sales. Raisy does the toil on both, and you keep the decisions. The gates close at the end of September.',
    keywords: ['Raisy closed beta', 'Raisy beta access', 'AI workforce platform beta', 'join Raisy', 'fundraising and sales at the same time'],
    content: (
      <>
        <p>
          <strong>
            Raising money takes three to six months. Your sales pipeline does not pause while you do it. Raisy does
            the toil on both, and you keep the decisions.
          </strong>
        </p>

        <CountdownTimer
          target={LIVE_GATE.target}
          kicker={LIVE_GATE.kicker}
          closedMessage={LIVE_GATE.closedMessage}
        />

        <h2>You already know this story</h2>
        <p>
          You spend Monday building an investor list. Tuesday writing emails nobody answers. Wednesday you realise
          you have not spoken to a customer in nine days.
        </p>
        <p>
          Then the round takes longer than planned, and by the time it closes your pipeline is cold and you are
          starting from zero on the thing that was supposed to make the round easier in the first place.
        </p>
        <p>
          Nobody sets out to run their company this way. It just happens, because both jobs are the same job. Find
          the right people, reach out well, follow up. And there is only one of you.
        </p>

        <h2>What Raisy does</h2>
        <p>You give it your URL. That is the whole setup.</p>
        <p>
          Raisy reads your company, works out who should be hearing from you, and writes the outreach:
          investors whose thesis and check size actually match your round, customers who look like the ones already
          buying from you. You read it, you change what you want, you send.{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Nothing leaves without you approving it
          </Link>
          .
        </p>
        <p>You get your evenings back and your pipeline stays warm through the raise.</p>

        <h2>Why this is a closed beta</h2>
        <p>
          Closed beta is the next group of companies running Raisy with us directly, not a signup form and a help
          centre. We sit with each founder while they raise. That only works at a certain size, so the group is
          capped and the waitlist decides who is in it.
        </p>
        <p>The alpha was the first group, run exactly this way. The beta is the next one.</p>
        <p>
          We are founders too. We are raising our own{' '}
          <Link to="/blog/startup-funding-stages-explained" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            pre-seed round
          </Link>{' '}
          with Raisy right now, so everything you use, we used first.
        </p>

        <h2>Join before the gates close</h2>
        <p>Your name and your email. That is it.</p>

        <WaitlistForm />

        <p className="text-sm text-slate-500 dark:text-slate-400">
          The gates close on 30 September 2026, and the waitlist closes with them.
        </p>

        <p>
          If you want the detail on how the outreach is built, read{' '}
          <Link to="/blog/from-url-to-department-in-one-paste" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            From URL to Department in One Paste
          </Link>
          . For the vocabulary we use for rounds, see{' '}
          <Link to="/blog/startup-funding-stages-explained" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            startup funding stages, explained
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink>.
        </p>
      </>
    ),
    faq: [
      { q: 'What is the waitlist for?', a: 'The closed beta. A small group of companies runs Raisy with us directly while they raise, so the group is capped and the waitlist decides who is in it.' },
      { q: 'What happened to the closed alpha?', a: 'It was the first group, and its gates are closed. The beta is the next group, and it is the one open now: the same list, worked through in order.' },
      { q: 'What happens after I join?', a: 'Your name and email go on the list and we work through it in order as capacity opens. The gates close at the end of September 2026; after that we stop taking new companies into this group.' },
      { q: 'Do I need to prepare anything?', a: 'No. You give Raisy your company URL. It reads the company, works out who should hear from you, and drafts the outreach; you review and approve before anything sends.' },
      { q: 'Is Raisy raising its own round on the product?', a: 'Yes. Ours is a pre-seed round, run on Raisy, so the outreach you would use is the outreach we are using.' },
      { q: 'Which stage vocabulary do you use?', a: 'Pre-seed, seed, Series A and later, as commonly defined. Pre-seed is the angel-led round: angel describes who writes the check, not a separate stage. We wrote the definitions out in a companion post, startup funding stages, explained.' },
    ],
    sources:
      'Raisy internal product facts: the closed-beta structure (a capped group, founder-by-founder onboarding) and the intake process are Raisy’s own commitments, not third-party data. Raisy’s own round is a pre-seed round run on the product; its terms, valuation and capacity are not published. The raise-duration window quoted in the opening (three to six months) is Raisy’s indicative planning range for a round at this stage, the same convention used in “Startup funding stages, explained”, where the ranges are declared as indicative defaults rather than measurements; it is not a measured statistic. Stage vocabulary follows that post. The closing date (30 September 2026) is Raisy’s own announced gate, carried by one constant in the code (`BETA_CLOSES_AT`) so the post, its countdown and the site banner always carry the same date. This post cites no benchmark, market-size or return figures, and makes no forecast about waitlist conversion or timing.',
  },
  // blog26 - the response clock. Every figure is cited published research, so this post carries no
  // product internals: no threshold, no internal metric name, no roadmap. The principle is public,
  // the configuration is not (blog manifest Rule 8).
  {
    slug: 'the-first-hour-decides',
    title: 'Lead Response Time: the 100x Cost of a Slow Reply',
    date: '2026-09-15',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/the-first-hour-decides.jpg',
    excerpt:
      'The average first response to an enquiry takes 42 hours, and 23 percent get no reply at all. The measured cost of a slow reply, and why the first hour decides.',
    keywords: [
      'lead response time',
      'speed to lead',
      'how fast should you respond to a lead',
      'first response time benchmark',
      'respond to investor emails faster',
    ],
    content: (
      <>
        <p>
          Of every hundred enquiries a company receives, <strong>23 get no reply at all</strong>, and
          the average first response takes <strong>42 hours</strong>. That is not noise in the funnel.
          It is the funnel.
        </p>
        <p>
          The same body of research explains why. A contact attempt made five minutes after an enquiry
          arrives has roughly <strong>100 times the odds</strong> of reaching that person as one made at
          thirty minutes. Waiting another five minutes, from five to ten, costs about five times on its
          own. Across the first hour the odds fall by more than a factor of ten. After twenty hours,
          each additional attempt carries a statistically significant <em>negative</em> contribution.
        </p>
        <p>
          So the value of a reply is not a flat line that slowly declines. It is a cliff in the first
          few minutes, and most companies are standing past the bottom of it.
        </p>

        <ChartZoom>
          <DecayOfSpeedChart />
        </ChartZoom>

        <h2>What we set out to change</h2>
        <p>
          The industry measures reply <em>rate</em>. Almost nobody manages reply <em>time</em>. Every
          outreach tool will report a response percentage, a sequence length and an open rate, and very
          few will tell you how long your last ten replies took to leave the building. The clock is the
          largest effect we have found in the outreach literature, and it is the one with no dashboard.
        </p>
        <p>
          One clarification, because &quot;timing&quot; names two different levers. This is not the
          best-time-to-send question, where the measured answer is modest. This is <em>latency</em>: how
          long passed between their message and yours. We do not rank latency against channel here,
          because they work at different rungs of the same funnel. Both are far larger than copy, which
          is the point: the hours after an enquiry arrives do more work than the words inside the reply.
        </p>
        <p>
          For a founder raising, both clocks are yours to run. The first is how fast the opening note
          goes out after an introduction, while the introduction is still warm. The second is how fast
          you answer the reply when it lands, which is the one you actually control and the one most
          often lost to a morning inbox check.
        </p>
        <p>
          That is not a technology limit. It is a measurement habit: timing is not in the report, so
          timing is not in the plan. We wrote about the size of that effect on the outreach side in{' '}
          <Link to="/blog/warm-intro-vs-cold-email" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            warm intro vs cold email
          </Link>
          , where channel turned out to matter most. Once the channel is right, this is the next term:
          not who you reached, but how fast you answered.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          <strong>
            Responsiveness is treated as a product behaviour rather than a personal virtue. The system
            watches the inbound channel continuously and has the next step prepared before a human looks
            at it.
          </strong>
        </p>
        <p>
          Behind that is an honest tension. The fastest possible reply is a worthless one: sending
          without reading damages your sending reputation and the relationship at the same time. So
          speed is not achieved by sending sooner. It is achieved by moving the preparation earlier, so
          that when the reply arrives the only remaining step is a decision. That is why the preparation
          is the work and the send is the easy part.
        </p>

        <h2>How it works</h2>
        <ul>
          <li>
            <strong>Watch continuously, do not check periodically.</strong> An inbox that is read twice
            a day imposes a half-day delay on every reply no matter how fast the writing is.
          </li>
          <li>
            <strong>Prepare before the reply arrives.</strong> The draft, the context and the suggested
            next step are assembled ahead of time, so the response window is spent deciding rather than
            drafting.
          </li>
          <li>
            <strong>Keep the decision small.</strong> One item, one choice: approve, edit or reject.
            A review screen with twelve things on it costs minutes per item, which is the same as being
            slow.
          </li>
          <li>
            <strong>Rank by priority, not by arrival.</strong> A queue ordered by what arrived first
            will serve whoever was loudest, not whoever matters most. The ordering has to be an explicit
            judgement, and it has to be visible so it can be argued with.
          </li>
          <li>
            <strong>Treat an open thread with no prepared next step as a defect.</strong> Not as
            a backlog item. The difference between those two framings is the whole difference between a
            system that answers in minutes and one that answers eventually.
          </li>
          <li>
            <strong>Learn from the outcome, not the activity.</strong> A reply that produced nothing
            should change how the next one is prepared. This is the only mechanism that makes the
            preparation worth doing earlier.
          </li>
        </ul>

        <h2>What speed is actually worth</h2>

        <ChartZoom>
          <CloseBySpeedChart />
        </ChartZoom>

        <p>
          The decay in the first hour is not only an early-funnel effect. Close rate moves with it all
          the way to the end: 32 percent when the first response goes out inside five minutes, and 12
          percent once it has taken more than a day. A twenty-point swing in close rate, decided by a
          clock rather than by the pitch.
        </p>

        <h2>What the user actually sees</h2>
        <p>
          An enquiry arrives and is read immediately. What is waiting for you is not a blank reply box
          but a prepared response with the context that makes it reviewable: who this is, what they
          asked, what we know about them from prior contact, and the suggested next step. You approve,
          edit or reject. Everything is logged, and every decision teaches the next preparation.
        </p>
        <p>
          You review one queue, one decision per item, and you never have to go looking. What you should
          never see is an unanswered item: a message waiting to be found, or a warm thread with nothing
          prepared behind it. A reply that says &quot;let&apos;s talk&quot; is the same conversation as
          the email that earned it, not a separate thing to chase.
        </p>

        <ChartZoom>
          <WhereRepliesLandChart />
        </ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          If the cliff is real, then two things follow that are easy to miss. The first is that
          <em>answering at all</em> is already a competitive advantage: nearly a quarter of enquiries
          get no response, and more than half of one 433-company audit took longer than five business
          days. The second is that speed compounds with persistence rather than replacing it. In one
          dataset of about 3.5 million leads, 93 percent of converted leads were reached by the sixth
          attempt, needing seven or more attempts was associated with being 45 percent less likely to
          convert, and half the leads were only ever called once. Fast first contact is what buys you
          the right to follow up.
        </p>
        <p>
          The same physics shows up in ageing deals: across 148,199 CRM opportunities, the correlation
          between how long an opportunity had been open and whether it was won was about{' '}
          <strong>-0.45</strong>. Older threads win less often, and the clock starts at the first reply
          you did not send.
        </p>

        <h2>What this post does not answer</h2>
        <ul>
          <li>
            <strong>Whether these numbers transfer to your market.</strong> Most of the corpus is B2B
            software and services. The direction is consistent across datasets and years; the exact
            multiples are not a promise about your list.
          </li>
          <li>
            <strong>Whether the cause is attention or intent.</strong> The studies measure the effect,
            not the mechanism. A reply that is slower can also mean a lead that was weaker to begin
            with, and the correlation between speed and outcome is not the same as speed causing the
            outcome.
          </li>
          <li>
            <strong>What happens when everyone is fast.</strong> If the whole market answered inside
            five minutes, part of this advantage would be competed away. These are measurements of a
            market that mostly is not fast.
          </li>
          <li>
            <strong>What your own reply time is.</strong> These are population numbers. The product
            reports your own timing back to you rather than asking you to plan against these priors.
          </li>
        </ul>

        <h2>A note on confidence</h2>
        <p>
          The 42-hour average, the 47-percent-tail distribution and the odds ratios from the Lead
          Response Management study are the strongest evidence here: a published Harvard Business
          Review audit of 2,241 companies, and a three-year study across six companies, 15,000+ leads
          and 100,000+ call attempts. The decay curve and the close-rate gradient come from large
          vendor datasets, where the shape is reproduced independently but the exact percentages vary
          with methodology, so we treat them as <strong>medium</strong>. Two studies disagree on how
          many companies answer within five minutes (0.1 percent and 7 percent), and we carry both
          rather than averaging them into a number neither one reported.
        </p>
        <p className="text-sm">
          <a
            href="/blog/the-first-hour-decides-data.csv"
            download
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Download every figure in this post (CSV, 37 rows, each with its source and confidence tier)
          </a>
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            See what is already prepared when the next reply lands at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'How fast should you respond to a lead?', a: 'The evidence points inside five minutes. Contact odds are roughly 100 times higher at five minutes than at thirty, and close rate runs 32 percent inside five minutes against 12 percent after a day. Most companies do not manage this number at all: the average first response is 42 hours.' },
      { q: 'Is this about sales, or does it apply to fundraising?', a: 'Both, and the mechanism is the same. Attention and urgency decay with time, so the first credible reply sets the frame. For a founder raising, an investor reply answered in the hour lands in a different conversation from one that waited until the next morning.' },
      { q: 'Does replying fast mean replying badly?', a: 'No, and that tension is the design question. Speed without preparation is a fast bad email. The preparation has to happen before the reply arrives, so that the only step left when it does is a decision.' },
      { q: 'Why do the odds decay so quickly?', a: 'The published studies measure the effect rather than the cause. The practical reading: a reply competes with whatever the recipient is doing right now, so the longer the delay, the more the moment has passed and the more likely the enquiry has been answered elsewhere.' },
      { q: 'What about follow-ups?', a: 'They work, within limits. In one dataset of about 3.5 million leads, 93 percent of converted leads were reached by the sixth attempt, needing seven or more attempts was associated with being 45 percent less likely to convert, and half the leads were only ever called once.' },
    ],
    sources:
      'Oldroyd, McElheran & Elkington, "The Short Life of Online Sales Leads", Harvard Business Review 89(3):28, March 2011: the 42-hour average first response, the response distribution, and the hourly qualification odds. Oldroyd (MIT Sloan) with InsideSales.com, Lead Response Management Study, 2007 (6 companies, 15,000+ leads, 100,000+ call attempts over three years): the 100x and 21x odds, the five-to-ten-minute loss, the first-hour decay, the twenty-hour negative contribution, and the time-of-day effect. Velocify (now ICE), "The Ultimate Contact Strategy" (~3.5M leads, 400+ companies): the decay curve and the persistence figures. XANT (InsideSales) Lead Response Study 2021 (55M sales activities, 5.7M inbound leads): the 8x five-minute conversion effect and the 0.1 percent engagement rate. Optifai Lead Response Time Benchmark, 939 B2B SaaS companies, Q2 2025 to Q1 2026: close rate by response speed. Drift/Salesloft Lead Response Survey, February 2017 (433 B2B companies) and RevenueHero (1,000 B2B SaaS companies, 2024): the modern audits, including the 7 percent within five minutes and the 63.5 percent that never replied. Chili Piper 2025 Demo Form Conversion Benchmark (4M form submissions): 30 percent to 66.7 percent when a booking step removes the wait. National College of Ireland, B2B sales opportunity win/loss prediction (148,199 CRM opportunities): the -0.45 correlation between opportunity age and winning. Data file behind every figure: playano-docs/raisy/projects/blog/assets/blog26/the_first_hour_decides.csv, mirrored to raisy_FE/src/components/public/data/firstHour.json. Verified 2026-09-15.',
  },
  // blog25 - channel science: where investments actually come from. Closes the gap blog15 (the
  // execution challenge), blog16 (the cost), blog18 (deliverability), blog23 (the scoring surface)
  // and blog24 (sizing and scheduling) all leave open. Three figures, all reading one committed data
  // file, and every figure states its own confidence tier.
  {
    slug: 'warm-intro-vs-cold-email',
    title: 'Warm Intro vs Cold Email: 1 in 10 Cheques Is Cold',
    date: '2026-09-15',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/warm-intro-vs-cold-email.jpg',
    excerpt:
      'About one in ten VC cheques starts from a cold email. The measured science of warm introductions, and what cold outreach is still good for.',
    keywords: [
      'warm intro vs cold email',
      'warm introduction to investors',
      'how to get investor introductions',
      'cold email investors response rate',
      'VC deal flow sources',
    ],
    content: (
      <>
        <p>
          Of every ten cheques venture capitalists write, about <strong>one</strong> starts from a cold
          email. The other nine start from networks, referrals, and the investor&apos;s own scouting.
        </p>
        <p>
          That share is not an opinion. It comes from a peer-reviewed survey of 885 venture capitalists at
          678 firms (Gompers, Gornall, Kaplan and Strebulaev, published in the Journal of Financial
          Economics, 2020). Roughly 30 percent of investments trace to professional networks, about 30
          percent to the investor&apos;s own proactive search, 20 percent to referrals from other
          investors, 8 percent to referrals from portfolio companies, and about 10 percent to cold inbound
          from founders.
        </p>
        <p>
          Most fundraising tooling is built for the 10 percent channel. This post is about the 90, and about
          what the 10 is still good for.
        </p>

        <ChartZoom>
          <WarmVsColdFlowChart />
        </ChartZoom>

        <h2>What we set out to change</h2>
        <p>
          A founder raising for the first time runs the process they can see, and the process they can see is
          a numbers game: build a list, write a template, send volume. The base rate underneath that plan is
          severe. The same survey shows a venture firm screens about 200 companies a year and makes about 4
          investments, a 2 percent yield before any relationship exists. Practitioner data adds the texture:
          top partners receive 50 to 200 unsolicited pitches a week and read the first two sentences.
        </p>
        <p>
          The usual answer is to optimize the email. We have written about{' '}
          <Link to="/blog/what-investor-outreach-actually-costs" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            what that email costs
          </Link>
          , and about why most tools only hand you the list in{' '}
          <Link to="/blog/the-investor-outreach-execution-challenge" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            the investor outreach execution challenge
          </Link>{' '}
          (both on the <BlogHubLink>Raisy blog</BlogHubLink>). Copy optimization is real, and its measured
          sizes are further down. But it is the smaller term, and treating it as the main term is how a
          founder spends six weeks tuning a channel that starts one cheque in ten.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          <strong>
            A raise is a networking exercise before it is a copywriting exercise. So Raisy builds warm paths
            first and sends cold email second.
          </strong>
        </p>
        <p>
          The core finding, across every dataset we trust: channel beats message by about an order of
          magnitude. DocSend&apos;s 2025 synthesis puts a warm introduction at roughly <strong>13 times</strong>{' '}
          the fundraise success rate of cold outreach. The best-measured copy improvements (real
          personalization, one follow-up, tighter length) land between 1.3 and 2.4 times. Ten times against
          two times. You tune the big term first.
        </p>
        <p>Warmth also changes what happens to the deck itself.</p>

        <ChartZoom>
          <WarmVsColdDoorChart />
        </ChartZoom>

        <h2>How it works (the technical layer)</h2>
        <ul>
          <li>
            <strong>Grade the introducer, not just the intro.</strong> A weak introduction behaves like cold
            email. In the VCWiz field experiment (arXiv:1806.03241), 301 facilitated, low-context intro
            requests produced 19 responses (6.3 percent), 5 affirmative replies, 1 connection, and{' '}
            <strong>zero investments</strong>. The multiplier lives in the quality of the person making the
            introduction, so we model introducer strength as a graded gate, roughly 1 to 13, rather than as
            a binary.
          </li>
          <li>
            <strong>Map the second degree.</strong> The path from a founder to a partner usually exists
            within two hops: a portfolio founder, a shared advisor, a co-investor from a previous round.
            Raisy surfaces these paths per target from its entity graph instead of leaving the founder to
            notice them.
          </li>
          <li>
            <strong>Earn the intro back with research.</strong> Reading a fund&apos;s portfolio and speaking
            to its companies first turns a cold request into a warm context. We call the pattern investor
            reverse diligence, and it is mechanical: pick the target, map the portfolio, talk to the founders
            inside it, then ask for the introduction with evidence that you did the work.
          </li>
          <li>
            <strong>Send the deck where the team slide leads.</strong> In an AngelList randomized experiment
            (Bernstein, Korteweg and Laws, Journal of Finance, 2017), early-stage investors responded to
            founding-team information, while traction numbers and lead-investor names showed no measurable
            effect. At this stage the team slide is the document; the warm intro is what gets it read.
          </li>
          <li>
            <strong>Respect the reputation premium.</strong> Founders accept 10 to 14 percent lower
            pre-money valuations from high-reputation firms, and offers from reputable firms are about three
            times more likely to be accepted (Hsu, Journal of Finance, 2004). The channel premium is priced
            on both sides of the table, which is why the warm path stays worth the work even when it is
            slower.
          </li>
          <li>
            <strong>Run cold as the precise floor, not the strategy.</strong> Cold is what fills the top of a
            warm graph, and precision is what makes it survivable: fit-targeted, verified, signal-specific
            lists return 5 to 15 percent replies, where generic blasts return under 1.5 percent.
          </li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          In the product this order is visible, not just advised. For each investor target, Raisy shows the
          path first: who can introduce you, through which node, with the research that makes the ask
          reasonable attached. Cold outreach runs underneath as the volume floor, with every email
          researched, approved by you, and logged. Nothing leaves without your approval, and the funnel
          reports both channels separately, because a reply earned warm and a reply earned cold carry
          different weight downstream.
        </p>

        <h2>The cold levers, measured</h2>
        <p>
          For the days when cold is the only door available, these are the sizes of the levers that still
          work, and one that stopped working.
        </p>

        <ChartZoom>
          <WarmVsColdLeverChart />
        </ChartZoom>

        <p>
          One follow-up lifts replies by <strong>65.8 percent</strong>, and 42 percent of all replies arrive
          after the first send (Backlinko/Pitchbox, 12 million emails). Personalizing the subject and body
          with something specific lifts replies by about <strong>142 percent</strong> combined, against 7 to
          9 percent for basic mail-merge. The response peak sits at <strong>75 to 100 words</strong> (51
          percent response) at a third-grade reading level (36 percent above college-level writing)
          (Boomerang, 40 million emails). And one popular trick is finished: first-name merge fields lifted
          opens 20 percent in a 2016 randomized trial and <strong>nothing</strong> in the 2024 replication.
          Tactics have half-lives. Channels compound.
        </p>
        <p className="text-sm">
          <a
            href="/blog/warm-intro-vs-cold-email-data.csv"
            download
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Download every figure in this post (CSV, 30 rows, each with its source and confidence tier)
          </a>
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          There is a comforting myth in fundraising advice: that the playing field is level if your email is
          good enough. The data says the field is made of relationships, and that relationships can be
          manufactured deliberately, with research and patience, by founders who have no network today. That
          is the actual levelling. A founder who treats introductions as engineering rather than luck can
          walk the same 58-contact, 40-meeting, twelve-and-a-half-week path DocSend measures for successful
          seed closes, and arrive warm at the meetings that decide it.
        </p>

        <h2>What this post does not answer</h2>
        <ul>
          <li>
            <strong>Whether the 13x figure is exact.</strong> It is platform data, published as marketing
            research rather than peer review. The direction is corroborated by the peer-reviewed deal-flow
            shares; the exact multiplier is medium confidence, and we label it that way wherever we use it.
          </li>
          <li>
            <strong>Whether warm paths exist for every founder.</strong> Some sectors and geographies have
            thinner graphs. Syndication platforms cut the geographic hub premium by about a third (NBER WP
            24777), and a third is not all of it.
          </li>
          <li>
            <strong>Whether a warm intro guarantees anything.</strong> It buys a reading, not a cheque. The
            meeting still has to be earned, and the company still has to clear the investor&apos;s own bar.
          </li>
          <li>
            <strong>What your personal funnel looks like.</strong> These are population numbers. Your reply
            rates will differ, which is why the product reports your own funnel back to you rather than
            asking you to plan against these priors.
          </li>
        </ul>

        <h2>A note on confidence</h2>
        <p>
          The deal-flow shares, the screening base rate, the AngelList team-signal experiment and the Hsu
          reputation premium are peer-reviewed, so we treat them as <strong>high</strong> confidence. The
          DocSend figures (the 13x multiplier, the review times, the conversion rates, the 58/40/12.5 close
          budget) are primary platform data published as marketing research, so <strong>medium</strong>. The
          cold-email lever sizes come from large vendor datasets of 12 to 40 million emails, where the
          direction is robust across independent datasets and the magnitudes vary with methodology, so also{' '}
          <strong>medium</strong>. The VCWiz experiment is a published field study with a small funnel, so{' '}
          <strong>medium-high</strong>. Where two sources disagree we say so in the figure note rather than
          averaging them silently.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            If you are raising, start from your warm paths rather than your email template. See yours mapped
            at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is cold email dead?', a: 'No. It is a floor with a known ceiling, useful for filling the top of a warm graph and for targets where no path exists. Precise cold (verified addresses, fit targeting, signal-specific personalization) returns 5 to 15 percent replies; generic volume returns under 1.5 percent and damages the domain you send from.' },
      { q: 'How many investors should I contact for a seed round?', a: 'DocSend\u2019s successful-close budget is about 58 investors contacted and 40 meetings over roughly 12.5 weeks. Warm-path conversion means you reach those numbers with fewer, better-chosen touches.' },
      { q: 'What is a good cold reply rate?', a: 'Vendor baselines range from under 1 percent to over 8 percent depending on methodology and year, and the channel decays by roughly 15 percent annually. We quote lifts and ratios rather than a universal baseline, because the baseline is not stable enough to plan against.' },
      { q: 'Does the introducer\u2019s identity really matter that much?', a: 'Yes, and it is measurable. The VCWiz experiment shows facilitated, low-context introductions performing at cold-email levels, while trusted-source introductions convert to meetings at 40 to 60 percent in practitioner data. The introduction is a signal, and the signal\u2019s strength is the introducer.' },
      { q: 'Where does the 90 percent figure come from?', a: 'A peer-reviewed survey of 885 venture capitalists at 678 firms, published in the Journal of Financial Economics in 2020. Roughly 30 percent of investments trace to professional networks, 30 percent to the investor\u2019s own search, 20 percent to investor referrals, 8 percent to portfolio-company referrals, and about 10 percent to cold inbound from founders.' },
    ],
    sources:
      'Gompers, Gornall, Kaplan & Strebulaev, "How Do Venture Capitalists Make Decisions?", Journal of Financial Economics 135(1):169-190, 2020 (NBER WP 22587): deal-flow shares, screening base rates. DocSend deck analytics and "The Warm Intro" 2025 synthesis: review times, conversion rates, close budgets, the 13x multiplier (platform data, medium confidence). VCWiz field experiment, arXiv:1806.03241. Bernstein, Korteweg & Laws, "Attracting Early-Stage Investors: Evidence from a Randomized Field Experiment", Journal of Finance, 2017. Hsu, "What Do Entrepreneurs Pay for Venture Capital Affiliation?", Journal of Finance, 2004. Gompers et al. via NBER WP 24777: syndication and the hub premium. Cold-email levers: Backlinko/Pitchbox "We Analyzed 12 Million Outreach Emails"; Boomerang 40M-email corpus; Woodpecker 20M+ dataset; Sahni, Wheeler & Chintagunta, Marketing Science (2016) and the Defau (2024) replication. Data file behind every figure: playano-docs/raisy/projects/blog/assets/blog25/warm_vs_cold_outreach.csv, mirrored to raisy_FE/src/components/public/data/warmVsCold.json. Verified 2026-09-15.',
  },
  // blog24 — the round announcement. The countdown below renders from the GENERATED
  // `roundSchedule.ts`, so this post and the company's own calendar cannot disagree about a
  // date (D-041/D-045). Publishing this post is what makes the announcement real: before it,
  // the deadline existed only in the model.
  {
    slug: 'same-money-fewer-deals',
    title: 'Pre-Seed Round Size: Same Money, 22% Fewer Deals',
    date: '2026-09-15',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/same-money-fewer-deals.jpg',
    excerpt:
      'Pre-seed put the same dollars into 22% fewer deals in Q2 2026. What that changes about sizing and scheduling a round, and the close date we publish for our own.',
    keywords: [
      'pre-seed round size',
      'how long does it take to raise a pre-seed round',
      'rolling SAFE close',
      'post-money SAFE valuation cap',
      'fundraising timeline',
    ],
    content: (
      <>
        <p>
          <strong>Pre-seed put the same money into fewer deals.</strong> In the second quarter of 2026, US pre-seed startups raised <strong>$3.19 billion across under 11,500 instruments</strong>. A year earlier they raised <strong>$3.22 billion across 14,825</strong>. Same capital, about <strong>22 percent fewer deals</strong>, and an average cheque that grew <strong>27 percent to a record $276,000</strong>.
        </p>
        <p>
          That changes how a founder should run a raise, and it is why we are publishing a closing date for our own round instead of leaving it open.
        </p>

        <h2>What changed in the market</h2>
        <p>
          A founder raising pre-seed in 2026 competes for <strong>fewer, larger cheques</strong>. Carta's State of Pre-Seed for Q2 2026 puts the average instrument at <strong>$276,000</strong>, a record and up <strong>27 percent</strong> year over year.
        </p>
        <ChartZoom>
          <PreseedChequeChart />
        </ChartZoom>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          The average pre-seed cheque rose from $217,000 to $276,000 in two years, a 27 percent step up. The size of the round did not change as much as the number of cheques that make it up. Source: Carta, State of Pre-Seed, Q2 2026.
        </p>
        <p>
          Most fundraising advice in that market is about reaching more investors: build the list, warm the intros, send more emails. When capital concentrates, reach stops being the constraint. <strong>Size</strong> and <strong>timing</strong> decide instead: how much cover the round buys, and whether the round has a date by which it closes.
        </p>
        <ChartZoom>
          <PreseedMoneyChart />
        </ChartZoom>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Total pre-seed capital deployed was $3.19B in Q2 2026 against $3.22B a year earlier, a change of about 1 percent. The market did not shrink. It concentrated.
        </p>
        <ChartZoom>
          <PreseedDealsChart />
        </ChartZoom>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          3,325 fewer pre-seed instruments were signed in Q2 2026 than in Q2 2025, a 22 percent drop. Fewer deals, the same money: that is what a larger average cheque looks like from the other side of the table.
        </p>
        <p className="text-sm">
          <a
            href="/blog/same-money-fewer-deals-data.csv"
            download
            className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2"
          >
            Download the figures behind these three charts (CSV, 4 rows)
          </a>
        </p>
        <p>
          An open-ended campaign has no clock, so it has no urgency, so the meetings drift. We wrote about the cost of that drift in{' '}
          <Link to="/blog/what-investor-outreach-actually-costs" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            what investor outreach actually costs
          </Link>
          , and about what a raise dashboard can hide even when every number on it is true, in{' '}
          <Link to="/blog/why-every-raise-dashboard-says-hot" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            why every raise dashboard says hot
          </Link>
          . Neither post covered the calendar, which is what this one does. See the rest of our{' '}
          <BlogHubLink>fundraising and product writing</BlogHubLink>.
        </p>

        <h2>Treat a round as a scheduled event</h2>
        <p>
          Two decisions follow from that, and both are checkable:
        </p>
        <ol>
          <li>
            <strong>Size the round to the runway it buys.</strong> The working standard is 12 to 18 months of cover, measured from the month the money actually lands rather than from the first month of your forecast. A round that buys 26 months is not a safety margin. It means you will not raise again for two years, which is the opposite of running a continuous process.
          </li>
          <li>
            <strong>Publish the closing date and keep it.</strong> A date everyone can see is the cheapest urgency available, and it costs nothing to honour.
          </li>
        </ol>
        <p>
          A pre-seed round can be closed this way because of how the market already works. In Q2 2026, <strong>93 percent of pre-seed rounds were structured as SAFEs</strong> (95 percent by capital), <strong>91 percent of those were post-money</strong>, and <strong>94 percent carried a valuation cap</strong>. A SAFE is not a one-shot event; it is an instrument that can be opened once and closed in tranches. That makes a rolling close ordinary rather than clever.
        </p>

        <h2>How it works</h2>
        <ul>
          <li>
            <strong>Open one instrument, not several.</strong> Two rounds mean two caps, which creates conversion ambiguity and gives a later close a reason to argue about price. One post-money SAFE, one cap, closed in tranches.
          </li>
          <li>
            <strong>Tie each close to a milestone, not to a date you hope for.</strong> The first close runs on the material you already have. A later close runs on the first real product numbers rather than on the plan.
          </li>
          <li>
            <strong>Publish the first close and nothing more.</strong> Announcing the first close gives the deadline its force. What capacity exists beyond it is a private matter between us and our investors, which is what lets a round flex upward without re-announcing a total.
          </li>
          <li>
            <strong>Measure the pipeline with two separate numbers.</strong> Coverage describes how much of the relevant pool is in range; engagement heat describes how warm each relationship is. They answer different questions, and merging them hides which one moved.
          </li>
          <li>
            <strong>Derive the published date from one file.</strong> The countdown on this page reads from a generated schedule, not from a sentence someone typed into a slide. One source of truth for a date is what stops the deck, the dashboard and the blog from disagreeing about the same deadline.
          </li>
        </ul>

        <h2>What you see here</h2>
        <p>
          <strong>A number and a deadline.</strong> We are opening the first close of our pre-seed round: <strong>$500,000, closing 15 November 2026.</strong> It is one post-money SAFE, closed in tranches, and it stays open after that close. We publish the first close and its date, and we do not publish capacity beyond it. That is what lets a round flex upward without re-announcing a total. When this round closes, the next round's dates go out in the same update.
        </p>
        <RoundCloseTimer
          className="my-8 rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900"
          kickerClassName="text-xs font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-400"
        />
        <p>
          <strong>That timer is not typed into this page.</strong> It reads the same generated schedule that drives our internal calendar. Move a date there and the timer moves with it, so it always agrees with what we told investors.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          A raise is a capability a company either has or does not. Treating it as a project that starts when the bank balance looks thin guarantees that every round happens under pressure, at the worst possible moment to negotiate. Treating it as a function that runs continuously, with a calendar, a pipeline and a published deadline, makes it a different company.
        </p>
        <p>
          That is also the honest read of the market data above. Capital concentrated into fewer, larger instruments rewards founders who arrive with a schedule and a sized ask, and it punishes founders who arrive with a list and a hope.
        </p>
        <p>
          If you are raising, start by writing your closing date down, then size the round to the cover it buys. You can see how we run that process on the product at{' '}
          <a href="https://raisy.team" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            raisy.team
          </a>
          .
        </p>

        <h2>What this post does not answer</h2>
        <ul>
          <li>
            <strong>Whether the concentration continues.</strong> Two quarters one year apart describe a direction, not a trend with a forecast attached.
          </li>
          <li>
            <strong>Whether a published closing date suits every raise.</strong> A round with no traction and no warm base may need a longer window; the calendar is a discipline, not a universal law.
          </li>
          <li>
            <strong>What the right size is for your company.</strong> The 12-to-18-month rule sizes a round to cover; it says nothing about how much you should be spending.
          </li>
          <li>
            <strong>Whether a tranched close suits every investor.</strong> Some institutional investors prefer a single priced round with a defined ownership percentage.
          </li>
        </ul>

        <h2>A note on confidence</h2>
        <p>
          The comparison at the top of this post is <strong>descriptive</strong>: $3.22 billion across 14,825 instruments, against $3.19 billion across more than 11,500, as published by Carta. We ran no significance test and we claim none. If deal counts genuinely did not change and the difference were only noise, a fall this large would be surprising. That is not the same as tested, and we would rather say which one this is.
        </p>
      </>
    ),
    faq: [
      {
        q: 'Is a $500,000 pre-seed round small?',
        a: 'Against the current market, no. The average pre-seed instrument was $276,000 in Q2 2026, so a $500,000 first close is roughly two average cheques, closed by a small number of angels rather than a crowd.',
      },
      {
        q: 'Why announce a first close rather than a total?',
        a: 'The first close is the concrete, near-term ask with a date attached. Capacity beyond it exists, and keeping it private lets the round flex upward without re-announcing a total. That is a normal pre-seed structure: 93% of pre-seed rounds are SAFEs, and a SAFE can close in tranches.',
      },
      {
        q: 'Does the timer change the terms?',
        a: 'No. A closing date creates urgency about when a decision happens. It changes nothing about the deal itself.',
      },
    ],
    sources:
      'Carta, State of Pre-Seed Q2 2026, published 2026-08-13 (carta.com/data/state-of-pre-seed-q2-2026/), covering capital invested, instrument count, average instrument size and SAFE share. Carta, "In the pre-seed market, valuation caps on new SAFEs keep getting bigger", published 2026-08-25 (carta.com/data/safe-valuation-caps-q2-2026/), covering post-money SAFE share and cap prevalence. Raisy\'s own round size and dates are company commitments, stated as facts of the announced deal; the numbers behind them are internal and are not published.',
  },
  {
    slug: 'self-maintaining-investor-data',
    title: "Your Investor Database Keeps Dying. Here's the Fix.",
    date: '2026-08-26',
    category: 'Product',
    series: 'The Raisy Blog',
    audience: { persona: 'BotBuilder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/self-maintaining-investor-data.jpg',
    excerpt:
      "A generic scraper reads one VC's site and hears \u201cEarly stage capital for developers\u201d instead of \u201cAbstraction Capital.\u201d Here's the enrichment strategy that keeps an investor database accurate, fresh, and self-maintaining.",
    keywords: [
      'investor database enrichment',
      'LLM data enrichment',
      'investor data accuracy',
      'self-maintaining CRM data',
      'per-column AI enrichment',
      'find and add investor',
    ],
    content: (
      <>
        <p>
          Investor databases rot. Every one of them. A fund changes its check size, adds a sector, moves a team
          member, or quietly edits its thesis, and your list still shows what someone scraped eighteen months ago.
          The standard fix is a re-scrape, which costs money, takes time, and usually repeats the original sin: a
          scraper that trusted a tagline and wrote the wrong name into your database.
        </p>

        <h2>What we saw in the live data</h2>
        <p>
          We caught that exact mistake live. A fund in our own database, <strong>Abstraction Capital</strong>,
          has a website whose <code className="text-sm">&lt;title&gt;</code> reads{' '}
          <em>&quot;Early stage capital for developers.&quot;</em> That is a tagline, not a name. A naive scraper
          (including the first version of our own) would have written &quot;Early stage capital for developers&quot; as
          the entity name. That breaks dedup, breaks search, and makes the whole list untrustworthy.
        </p>

        <h2>The strategy: scrape once, enrich each column in its own way</h2>
        <ol className="list-decimal pl-5 space-y-2">
          <li><strong>Emails come from Hunter.io.</strong> Verifying an email address is a solved task with a dedicated tool. We do not reinvent it.</li>
          <li><strong>Every other column is found &quot;in its own way&quot; by an LLM.</strong> Stage focus lives in the thesis text. Check size lives in the &quot;how we invest&quot; section. Sector focus lives in the portfolio. LinkedIn lives in the page&apos;s social links. The enrichment prompt asks each column its own question against the fund&apos;s own website, and only fills empty cells, never overwriting data a human or a dedicated tool already provided.</li>
          <li><strong>Run once, track it.</strong> Every enrichment run stamps the profile with its time; the next run skips it. No re-scraping what we already did, accuracy and cost both come from not repeating work.</li>
          <li><strong>The user is the curator.</strong> Paste a fund&apos;s URL to <em>find &amp; add</em> it; take the name from the page&apos;s own declared brand, and a tiny fast model only when the page hides it. Then one click per investor <em>completes the details</em>.</li>
        </ol>

        <h2>Full control, not a black box</h2>
        <p>
          The results are a filter bar, not a frozen list: geography down to a specific country, sector/focus,{' '}
          <em>only investors with verified emails</em>, a data-quality floor, and a name search. Every change re-runs
          the query with hard filters: the user decides who is in the list, not a scoring formula. A shrinking funnel
          shows it live: 116 in the database → 38 in your geography → 12 after filters → top 10 ranked. When a column
          is absent, the user sees exactly which one and fills it with one click.
        </p>

        <h2>Why this is the part clients value</h2>
        <p>
          A database you can maintain is a database you can trust, and a database you can trust is one you will
          actually use: which is what makes outreach, CRM, and analytics work at all. The strategy is cheap by design
          (LLM calls only where a page hides its facts), honest by design (if the name is not there, we say so instead of
          inventing a name), and user-controlled by design (hard filters, one-click fills, loud failures). See the{' '}
          <Link to="/blog/what-investor-outreach-actually-costs" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            cost model
          </Link>{' '}
          for what that trust is worth. Browse the <BlogHubLink>blog</BlogHubLink>.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Keep your investor list alive at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'How is email enrichment different from the rest?', a: 'Email verification is a solved task with a dedicated tool (Hunter.io), so it stays on that service. The LLM enrichment covers every other column: stage, check size, sector, description, city, LinkedIn.' },
      { q: 'Will Raisy re-scrape investors I already enriched?', a: 'No. Every run is stamped, and the next run skips investors already enriched unless you explicitly force it. Nothing is re-scraped without a reason.' },
      { q: 'What if a page does not declare its name?', a: 'A tiny, fast model reads the page content to name the fund. If the name still does not emerge, Raisy says so loudly instead of inventing one: a wrong name is worse than no name.' },
      { q: 'Can I control the list myself?', a: 'Yes. Hard filters for geography (down to a country), sector, verified-email-only, and data quality, plus a name search. The user decides who is in the list.' },
      { q: 'Does enrichment overwrite data I entered?', a: 'Never. The LLM fills only empty cells. Anything a human or a dedicated tool has already provided is left untouched.' },
    ],
    sources:
      'Raisy Project-080/090 (investor outreach + investor database harvesting) execution notes; live verification of the enrichment ladder (fetch → deterministic identity → flash-lite name fallback → per-column fill) on real fund URLs; decision records D-060 (name sourcing) and D-062 (full-control filters) in project080_investor_outreach_e2e.md.',
  },
  {
    slug: 'txt-html-or-neither',
    title: 'Plain Text vs HTML Email: Which Passes 4 Filters?',
    date: '2026-08-26',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/txt-html-or-neither.jpg',
    excerpt:
      'Cold email passes four independent spam filters, not one. We break down what each layer checks and why plain text and HTML trip it differently.',
    keywords: [
      'plain text vs HTML email',
      'email spam filter layers',
      'cold email deliverability',
      'avoid spam folder',
      'email authentication SPF DKIM DMARC',
    ],
    content: (
      <>
        <p>
          Every founder who has sent a cold email has asked the same question at 11pm: should this be
          plain text or a designed HTML email? The honest answer is that you are asking the wrong
          question, because &ldquo;spam filter&rdquo; is not one gate. It is at least four,
          running in sequence, checking different things, and a message can pass three of them and
          still die on the fourth.
        </p>

        <h2>What we set out to change</h2>
        <p>
          The plain-text-vs-HTML debate online is a mess of contradictory advice, and the reason is not
          that anyone is lying. It&rsquo;s that different sources are measuring different filters.
          One well-cited comparison shows plain-text cold emails outperforming designed HTML by roughly
          15&ndash;25% in reply rate in aggregate sends, while a separate finding from the same space
          reports plain text emails being marked spam more often than &ldquo;high-quality&rdquo; HTML in
          certain samples. Both can be true at once, because &ldquo;reply rate&rdquo; and &ldquo;marked as
          spam&rdquo; are outcomes of different filter layers, and &ldquo;designed HTML&rdquo; and
          &ldquo;quality HTML&rdquo; are not the same artifact as the generic marketing template most
          tools default to.
        </p>
        <p>
          The deeper pattern: authentication is now table stakes, not a nice-to-have. As of February
          2024, Gmail and Yahoo require senders of 5,000+ daily messages to authenticate with SPF, DKIM,
          and DMARC, support one-click unsubscribe, and keep their reported spam complaint rate under
          0.3%, with enforcement escalating further from November 2025 onward. A perfectly worded
          plain-text email from an unauthenticated domain fails before a human or a classifier ever reads
          a word of it.
        </p>
        <p>
          The third pattern is that filters disagree with each other. Global average inbox placement
          across all providers sat at 87.25% in 2025, meaning roughly one in eight legitimate
          commercial emails never reaches an inbox at all, but that average hides a wide spread:
          Gmail placed 89.8% of mail in the inbox while Outlook placed only 75.6%. The same email, the
          same list, the same sender: different outcomes depending purely on which mailbox provider&rsquo;s
          filter stack it hit.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          Raisy treats &ldquo;will this land&rdquo; as a pipeline question, not a copywriting question,
          because that is what the evidence says it actually is. Three principles follow.
        </p>
        <p>
          <strong>Name the filter you&rsquo;re optimizing for, because there isn&rsquo;t just one.</strong>{' '}
          A message is evaluated at the network/gateway level (is this connection and sender IP
          trustworthy at all), at the mailbox-provider level (does this look like wanted mail to a model
          trained on billions of messages), at the inbox-tab-classification level (is this
          &ldquo;Primary&rdquo; or &ldquo;Promotions&rdquo;), and at the client level (has this specific
          recipient&rsquo;s own rules or training flagged senders like you before). Optimizing format for
          one layer can actively hurt you at another, heavy HTML can read as more
          &ldquo;legitimate marketing&rdquo; to an automated bulk-sender classifier while simultaneously
          reading as less personal to the human deciding whether to reply.
        </p>

        <ChartZoom>
          <SpamFilterLayersChart />
        </ChartZoom>

        <p>
          <strong>Authentication is the floor, not a layer to skip past.</strong> Every send{' '}
          <Link to="/blog/what-investor-outreach-actually-costs" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Raisy handles for a founder passes a compliance and authentication check
          </Link>{' '}
          before it queues, the same principle as{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            the human-approval gate
          </Link>{' '}
          applied to the sending pipe itself: nothing goes out that would trip the first filter on
          arrival.
        </p>
        <p>
          <strong>Format follows intent, not habit.</strong> A first-touch investor email should read
          like a person wrote it, plain text or lightly formatted multipart, because that
          is both what research on cold outreach supports and what recipients say they want (two-thirds
          of recipients in one industry survey said they don&rsquo;t mind AI assistance in a cold email
          as long as it still reads human; the same logic extends to formatting). A newsletter or product
          update is a different intent with a different filter risk, and multipart MIME (a plain-text
          version bundled with the HTML one) is the standard middle path there.
        </p>

        <h2>How it works (the technical layer)</h2>
        <ul>
          <li>
            <strong>Network / gateway filtering.</strong> Before content is even parsed, the receiving
            mail server checks the connecting IP&rsquo;s reputation, reverse DNS, and rate of connection
           , this is where a brand-new sending domain with no history gets throttled or greylisted
            regardless of what the email says.
          </li>
          <li>
            <strong>Authentication.</strong> SPF, DKIM, and DMARC are checked next. Gmail&rsquo;s own
            guidelines set the bulk-sender bar explicitly: authenticate, keep spam complaints under 0.3%
            (target under 0.1%), and support one-click unsubscribe, miss any of these and the
            message can be rejected outright, independent of format.
          </li>
          <li>
            <strong>Mailbox-provider machine-learning classification.</strong> Gmail alone runs its spam
            classifiers, built on TensorFlow, across on the order of 15 billion messages a day, weighing
            domain and IP reputation, authentication consistency, and, critically:
            recipient engagement signals (opens, replies, deletes-without-reading, spam reports) that
            Gmail feeds back into the model. This is the layer where format matters most, and where it
            cuts both ways: a tracking pixel is itself a request to an external server, and dropping it
            entirely correlates with meaningfully higher reply rates in aggregate data (7.4% vs 4.4%, a
            68% relative lift, across a 31-million-email 2025 sample), though a pixel is a weak
            signal on its own and the effect is measured at the sequence level, not proven as a single-
            message causal mechanism.
          </li>
          <li>
            <strong>Content and structure heuristics.</strong> Independent of the ML layer, rule-based
            systems like the open-source SpamAssassin explicitly score image-to-text ratio and
            image-only bodies, its published rule family fires around an 0.8 image-to-body-area
            ratio and scales up for image-heavy, low-text messages. This is a legacy from spammers
            historically hiding spam-trigger words inside images to dodge text scanners; it now penalizes
            any heavily designed HTML email that leans on graphics over text, format aside from intent.
          </li>
          <li>
            <strong>Client-side rules and personal filtering.</strong> The last gate is local: Outlook
            and Apple Mail on desktop support per-user client-side rules layered on top of whatever the
            provider already decided, and this layer is invisible to the sender entirely. It is
            trained (or configured) per recipient, one inbox at a time.
          </li>
        </ul>

        <ChartZoom>
          <EmailReplyRateDeltaChart />
        </ChartZoom>

        <h2>What the user actually sees</h2>
        <p>
          A founder using <BlogHubLink>Raisy&rsquo;s investor outreach flow</BlogHubLink> doesn&rsquo;t
          choose &ldquo;plain text or HTML&rdquo; from a dropdown and hope. The system already defaults
          first-touch investor emails to a lightly formatted, mostly-plain structure, because that
          is the format that survives the machine-learning engagement layer (recipients treat it as
          correspondence, not a campaign) without failing the authentication or gateway layers, since
          every send already goes out authenticated from a warmed domain per the compliance gate. The
          founder sees one send button; underneath it, four separate checks already ran.
        </p>

        <h2>Why this matters beyond convenience</h2>

        <EmailFormatVerdictChart />

        <p>
          The plain-text-vs-HTML framing treats deliverability as a single knob a founder can turn
          correctly if they just pick the right template. The evidence says otherwise: inbox placement
          varies by 14 points between major providers on the exact same message, DMARC enforcement sits
          at barely half of top domains even in 2026 (and most of that half is still monitor-only, not
          enforced), and the layer that most decides outcome, recipient engagement, is
          invisible to the sender until after the fact. Founders don&rsquo;t need a stronger opinion on
          HTML; they need each of the four gates handled correctly, in order, before the question of
          wording even comes up.
        </p>
        <p>
          Every number above reads from one committed data file, download it directly:{' '}
          <a href="/blog/raisy_email_format_deliverability_data.xlsx" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            raisy_email_format_deliverability_data.xlsx
          </a>
          .
        </p>
        <p>
          <strong>What this post does not answer:</strong> it does not report Raisy&rsquo;s own live
          inbox-placement numbers (the compliance and authentication gate is verified pre-send;
          production placement data is a follow-up once real campaigns run at volume, per{' '}
          <Link to="/blog/what-investor-outreach-actually-costs" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            the outreach-cost post
          </Link>
          ); it does not test a controlled plain-text-vs-HTML split on identical send infrastructure (the
          cited comparisons are industry aggregate data, not a single controlled experiment); and it does
          not cover client-side filter training in detail, since that layer is per-recipient and outside
          any sender&rsquo;s visibility by design.
        </p>
        <p className="mt-8">
          <a
            href="https://raisy.team"
            className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg"
          >
            See your compliance and authentication status before your first investor email, raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      {
        q: 'Should I ever use HTML for cold outreach?',
        a: 'Multipart MIME, an HTML version bundled with a plain-text fallback: is standard practice for regular marketing and update email, since it signals to filters that the sender supports both renderings. For a true first-touch cold message, plain or lightly-formatted text is the safer default, because the engagement-based ML layer rewards messages that read as personal correspondence.',
      },
      {
        q: 'Does removing the tracking pixel actually help?',
        a: 'In the 2025 Hunter.io dataset (31 million emails), sequences without an open-tracking pixel saw a 68% relative reply-rate lift (7.4% vs 4.4%) compared to sequences with tracking enabled. That is a correlation across many sends, not proof that any single pixel sank any single email, but it is consistent with a pixel being one more external-request signal a filter can weigh.',
      },
      {
        q: 'Is SPF/DKIM/DMARC really mandatory?',
        a: 'For any sender doing 5,000+ messages a day to Gmail or Yahoo addresses, yes, as of February 2024, with enforcement escalating further from November 2025. Below that volume it is not contractually mandatory, but the same authentication is still what the network and reputation layers check first.',
      },
      {
        q: 'Why does the same email land differently in Gmail vs Outlook?',
        a: 'Each provider runs its own model, trained on its own users’ engagement data, with its own reputation thresholds. 2025 data shows Gmail placing 89.8% of tracked mail in the inbox against Outlook’s 75.6%: the input is identical; the classifiers and the audiences behind them are not.',
      },
    ],
    sources:
      'Validity, "2026 Email Deliverability Benchmark Report" (global and per-provider inbox placement rates); Google, official Gmail sender guidelines and Postmaster Tools FAQ, support.google.com/mail/answer/14229414 (bulk-sender authentication mandate, 0.3%/0.1% spam-rate thresholds, effective 2024-02-01, enforcement escalation November 2025); Google Workspace Blog, "An overview of Gmail\'s spam filters" (TensorFlow-based classification architecture, ~15B messages/day); Hunter.io, "State of Cold Email 2026" report (31 million emails sent by Hunter.io users in 2025); Hunter.io blog, "Is HTML Harming Your Cold Email Deliverability?" (internal HTML-vs-plain-text bounce comparison, reported as a vendor finding, not a controlled study); Apache SpamAssassin project documentation (HTML_IMAGE_RATIO and HTML_IMAGE_ONLY rule families, publicly inspectable open-source ruleset); DMARC adoption tracking compiled from public DNS records by email-authentication vendors (52.1% of top 1.8M domains publish DMARC, over half at p=none). Full dataset: raisy/projects/blog/assets/blog18/raisy_email_format_deliverability_data.json.',
  },
  {
    slug: 'startup-funding-stages-explained',
    title: 'Startup Funding Stages, and Who Writes the Cheque',
    date: '2026-08-26',
    category: 'Education',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Beginner', depth: 'Overview' },
    image: '/blog/startup-funding-stages-explained.jpg',
    excerpt:
      'Pre-seed, seed, Series A, B, C: what each round actually means, who writes the check, how big the tickets are, and what you need to show before you raise it.',
    keywords: [
      'startup funding stages',
      'pre-seed vs seed',
      'Series A explained',
      'investor ticket size',
      'when to raise funding',
    ],
    content: (
      <>
        <p>
          Fundraising has its own vocabulary, and the words are load-bearing. A pre-seed angel and a
          Series B growth fund behave completely differently: different expectations, different
          check sizes, different diligence. If you pitch the wrong stage, you burn your best shot
          with an investor who was never a fit.
        </p>
        <p>
          The rounds happen in a fixed order, and each one maps to where the company actually is.
          Explore the funnel below, click a stage.
        </p>

        <FundingStagesExplorer />

        <h2>Order matters more than the name</h2>
        <p>
          The labels are milestones on a maturity curve, not marketing. Pre-seed and seed back the
          team and the first traction; Series A is the first time a fund underwrites a repeatable
          business model; Series B and C scale something that already works. Raising at the wrong
          stage means a mismatch with the investors you contact, the exact mismatch a stage-matched list
          funnel is built to avoid.
        </p>

        <h2>Why Raisy cares</h2>
        <p>
          Raisy runs investor outreach as a process. The entity profile derives the raising stage,
          target geography, suggested amount and the type of investor you should contact, then the
          funnel includes only the investors whose stage and ticket fit. You stay at the decision
          gate; the list is prepared for you.
        </p>
        <p className="mt-8">
          <Link
            to="/blog/the-raisy-beta-is-open"
            className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg"
          >
            Start preparing your raise
          </Link>
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Draft post, the cover image and final copy are still in progress. The interactive block
          above is live.
        </p>
      </>
    ),
    faq: [
      { q: 'Is pre-seed the same as an angel round?', a: 'Practically yes. Pre-seed is the earliest round, and it is typically led by angel investors, friends and family, accelerators, and micro-VC funds. “Angel” describes who writes the check, not a separate stage.' },
      { q: 'What do I need before a Series A?', a: 'Real revenue, repeatable growth, and sane unit economics. Series A is the first round where investors underwrite a scalable business model rather than just the team.' },
      { q: 'Are these raise amounts fixed?', a: 'No. They are indicative ranges that vary by region and industry. Use them as a starting point for planning, not as a promise.' },
    ],
    sources:
      'Investopedia: “What Is Series A Financing?” and “Seed Capital” (investopedia.com/terms/s/seriesa.asp, /terms/s/seedcapital.asp), accessed 2026-08-26. All raise and ticket-size ranges in the interactive funnel and table are Raisy’s indicative product defaults, read from the single source file `src/components/public/data/fundingStages.json` (the same file the product’s raise-profile prefill uses): structural model defaults, not measurements, and they vary by region and industry. Figure decision: D-058 (ECharts funnel + React detail panel, RULES_BLOG_POST Rule 42).',
  },
  {
    slug: 'the-raisy-waitlist-is-open',
    title: 'Raisy Waitlist Is Open: Fundraise and Sell at Once',
    date: '2026-08-25',
    category: 'Announcement',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Beginner', depth: 'Overview' },
    image: '/blog/the-raisy-waitlist-is-open.jpg',
    excerpt:
      'Raising takes months you should be spending on sales. Raisy does the toil on both, and you keep the decisions. The waitlist closes soon.',
    keywords: ['Raisy waitlist', 'AI workforce platform waitlist', 'Raisy closed alpha', 'join Raisy', 'fundraising and sales at the same time'],
    content: (
      <>
        <p>
          <strong>
            Raising money takes three to six months. Your sales pipeline does not pause while you do it. Raisy does
            the toil on both, and you keep the decisions.
          </strong>
        </p>

        <CountdownTimer />

        <h2>You already know this story</h2>
        <p>
          You spend Monday building an investor list. Tuesday writing emails nobody answers. Wednesday you realise
          you have not spoken to a customer in nine days.
        </p>
        <p>
          Then the round takes longer than planned, and by the time it closes your pipeline is cold and you are
          starting from zero on the thing that was supposed to make the round easier in the first place.
        </p>
        <p>
          Nobody sets out to run their company this way. It just happens, because both jobs are the same job. Find
          the right people, reach out well, follow up. And there is only one of you.
        </p>

        <h2>What Raisy does</h2>
        <p>You give it your URL. That is the whole setup.</p>
        <p>
          Raisy reads your company, works out who should be hearing from you, and writes the outreach:
          investors whose thesis and check size actually match your round, customers who look like the ones already
          buying from you. You read it, you change what you want, you send.{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Nothing leaves without you approving it
          </Link>
          .
        </p>
        <p>You get your evenings back and your pipeline stays warm through the raise.</p>

        <h2>Why this is closed for now</h2>
        <p>
          Closed alpha is a small group of companies running Raisy with us directly, not a signup form and a help
          centre. We sit with each founder while they raise. That only works at a certain size, so the group is
          capped and the waitlist decides who is in it.
        </p>
        <p>
          We are founders too. We are raising our own{' '}
          <Link to="/blog/startup-funding-stages-explained" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            pre-seed round
          </Link>{' '}
          with Raisy right now, so everything you use, we used first.
        </p>

        <h2>Join before it closes</h2>
        <p>Your name and your email. That is it.</p>

        <WaitlistForm />

        <p className="text-sm text-slate-500 dark:text-slate-400">
          The next intake is the{' '}
          <Link to="/blog/the-raisy-beta-is-open" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            closed beta
          </Link>
          , and the gates close on 30 September 2026.
        </p>

        <p>
          If you want the detail on how the outreach is built, read{' '}
          <Link to="/blog/from-url-to-department-in-one-paste" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            From URL to Department in One Paste
          </Link>
          . For the vocabulary we use for rounds, see{' '}
          <Link to="/blog/startup-funding-stages-explained" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            startup funding stages, explained
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink>.
        </p>
      </>
    ),
    faq: [
      { q: 'What is the waitlist for?', a: 'The closed alpha. A small group of companies runs Raisy with us directly while they raise, so the group is capped and the waitlist decides who is in it.' },
      { q: 'What happens after I join?', a: 'Your name and email go on the list and we work through it in order as capacity opens. The next group is the closed beta, and its gates close on 30 September 2026.' },
      { q: 'Do I need to prepare anything?', a: 'No. You give Raisy your company URL. It reads the company, works out who should hear from you, and drafts the outreach; you review and approve before anything sends.' },
      { q: 'Is Raisy raising its own round on the product?', a: 'Yes. Ours is a pre-seed round, run on Raisy, so the outreach you would use is the outreach we are using.' },
      { q: 'Which stage vocabulary do you use?', a: 'Pre-seed, seed, Series A and later, as commonly defined. Pre-seed is the angel-led round: angel describes who writes the check, not a separate stage. We wrote the definitions out in a companion post, startup funding stages, explained.' },
    ],
    sources:
      'Raisy internal product facts: the closed-alpha structure (a capped group, founder-by-founder onboarding) and the intake process are Raisy’s own commitments, not third-party data. Raisy’s own round is a pre-seed round run on the product; its terms, valuation and capacity are not published. The raise-duration window quoted in the opening (three to six months) is Raisy’s indicative planning range for a round at this stage, the same convention used in “Startup funding stages, explained”, where the ranges are declared as indicative defaults rather than measurements; it is not a measured statistic. Stage vocabulary follows that post. This post cites no benchmark, market-size or return figures, and makes no forecast about waitlist conversion or timing.',
  },
  {
    slug: 'what-investor-outreach-actually-costs',
    title: 'Investor Outreach Cost: the Full Price List',
    date: '2026-08-23',
    category: 'Product',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/what-investor-outreach-actually-costs.jpg',
    excerpt:
      'What does reaching an investor really cost? The DIY stack, the hidden infrastructure, and Raisy’s full price list: with an interactive plan finder.',
    keywords: ['investor outreach cost', 'cold email deliverability', 'investor outreach pricing', 'fundraising email cost', 'domain warmup'],
    content: (
      <>
        <p>
          What does it cost to reach an investor: really? Not the price of an email (nearly zero), but the
          price of that email being found, verified, compliant, and actually seen. Our free tier gives every founder
          five investor reachouts, and behind them sits an infrastructure most founders never have to think about
         : that is the point. This post lays out what it takes, what it costs to do yourself, and exactly what
          we charge for it.
        </p>

        <h2>What we set out to change</h2>
        <p>
          When we scoped investor outreach, the simple reading was: write a good email, send it to five well-matched
          investors, done. The{' '}
          <Link to="/blog/the-investor-outreach-execution-challenge" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            execution challenge post
          </Link>{' '}
          covers why lists and generic sales tools fall short for a raise (it and the rest of the series live on the{' '}
          <BlogHubLink>blog</BlogHubLink>). When we started building, we found a layer of quiet practicalities
          underneath, the kind every founder deserves to have handled for them.
        </p>
        <p>
          <strong>Delivery is invisible until it works.</strong> An email can be &quot;sent&quot; perfectly and still
          never be seen: new sending domains need time to earn trust with email providers, and that warm-up
          period that does not compress. We wanted your first impression with an investor to actually arrive.
        </p>
        <p>
          <strong>Every country plays by its own rules.</strong> Reaching out to investors is perfectly legal
          everywhere we researched, but the <em>how</em> differs. The US asks for a working unsubscribe link
          and a real postal address. Germany and several neighbours expect prior consent. Canada wants a documented
          reason before the first email. The UK adds an extra layer of care around anything investment-related. One
          &quot;send&quot; button has to quietly know all of this.
        </p>
        <p>
          <strong>Investor conversations have their own etiquette, and their own rulebook.</strong> A friendly
          first note is welcome everywhere. An email that reads like an offer sheet is a different kind of document
          with different rules. Knowing where that line sits, and keeping every first touch on the welcome side of
          it, turned out to be its own discipline.
        </p>
        <p>
          <strong>Lists age like milk, not wine.</strong> Even professionally verified investor lists bounce far more
          often than their vendors suggest, and business contacts naturally decay a few percent every month. Sending
          to a stale list doesn&apos;t just waste a stamp. It teaches email providers to distrust the sender.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          So we built the five emails as a peace-of-mind product, not a template feature. Three decisions carry the
          weight.
        </p>
        <p>
          <strong>One careful gate, keyed by intent and country.</strong> Every send, including the preview you
          approve: passes a compliance check that understands two things: where your recipient is, and what
          kind of email this is. A first hello to an investor is treated differently from a sales pitch, and a
          recipient in Munich is treated differently from one in Miami. If anything is absent, the gate says so in
          plain language before anything leaves, never after.
        </p>
        <p>
          <strong>You send; we prepare.</strong> Investors reply to founders, not platforms, so every email
          goes from your own mailbox, under your own name, at your pace. Raisy does the research, the ranking, the
          drafting, the checking, and the record-keeping. You approve every word. It is the same rule as everywhere
          else in Raisy,{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            humans decide, bots prepare
          </Link>{' '}
         , applied to the most visible channel your company has.
        </p>
        <p>
          <strong>Timing is part of the service.</strong> New sending infrastructure needs roughly a month of steady,
          honest use before email providers fully trust it. Rather than letting you discover that after the fact, we
          treat timing as something the platform manages and schedules around, so when your email matters, it
          lands.
        </p>

        <h2>How it works (the technical layer)</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>A ranked investor funnel.</strong> An external investor list becomes structured profiles, scored against your stage, sector, and geography. You see the ranking and the reasoning, never a black box.</li>
          <li><strong>A compliance gate on every send and every preview.</strong> The gate resolves the recipient&apos;s country and the email&apos;s intent, then applies the matching rules: consent-first countries ask for a documented basis, the UK investor path asks for a recorded exemption, and first touches stay in friendly, teaser-style framing by default.</li>
          <li><strong>Verification right before sending.</strong> Addresses are checked at send time, not at import time, and role addresses and known risk patterns are filtered out, because a list that was fine in March is not necessarily fine in June.</li>
          <li><strong>Sensible sending limits, built in.</strong> Per-mailbox daily caps, gentle ramp-up schedules, and the email providers&apos; own published thresholds are enforced by the platform, so a good week never accidentally becomes a bad reputation.</li>
          <li><strong>A record of everything.</strong> Approved, sent, answered: the full trail lives in your company record, so any future question has a clear, documented answer.</li>
        </ul>

        <ChartZoom><OutreachCostStackChart /></ChartZoom>

        <h2>What it costs, and what you&apos;re actually investing in</h2>
        <p>Here is the honest arithmetic of assembling this yourself, before your first email lands:</p>
        <div className="my-6 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-left">
                <th className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-200">Item</th>
                <th className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-200">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              <tr><td className="px-4 py-2.5">Sequencer / sending tool</td><td className="px-4 py-2.5">$39&ndash;97 / month</td></tr>
              <tr><td className="px-4 py-2.5">Email find + verification (Hunter)</td><td className="px-4 py-2.5">$0.04&ndash;0.15 per investor</td></tr>
              <tr><td className="px-4 py-2.5">Sending domains (2&ndash;4)</td><td className="px-4 py-2.5">~$11 / year each</td></tr>
              <tr><td className="px-4 py-2.5">Real mailboxes (2&ndash;3 per domain)</td><td className="px-4 py-2.5">~$6&ndash;7 / month each</td></tr>
              <tr><td className="px-4 py-2.5">All-in DIY stack</td><td className="px-4 py-2.5">$80&ndash;320 / month</td></tr>
              <tr><td className="px-4 py-2.5">Warm-up before full volume</td><td className="px-4 py-2.5">30&ndash;45 days, not compressible</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Every number in this table and in both figures reads from one data file, download it as{' '}
          <a href="/blog/raisy_outreach_infra_costs.xlsx" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            XLSX
          </a>{' '}
          or{' '}
          <a href="/blog/raisy_outreach_infra_costs.csv" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            CSV
          </a>
          .
        </p>
        <p>
          The subscription number is survivable, $80&ndash;320 a month is not what makes this hard. What the
          stack price leaves out is the calendar month of warm-up, the list care that never ends, and the quiet
          discipline of staying on the right side of rules that carry real weight, the kind of weight measured
          in thousands per email in the US and far more in Europe and Canada. None of that is meant to worry you; it
          is meant to explain why we took it seriously so early.
        </p>
        <p>
          That is what the paid tier is an investment in. Sending email itself costs almost nothing. What you are
          investing in is the gate that knows the difference between Munich and Miami, a list you can trust,
          infrastructure that is already warmed up, a documented trail behind every send, and the confidence that the
          natural outcome of your outreach is a good conversation. A full-service fundraising agency charges
          $5,000&ndash;25,000 a month, much of it for exactly this execution layer. We think that layer should cost
          like software.
        </p>

        <h2>What Raisy charges, and why</h2>
        <div className="my-4 rounded-xl border-2 border-red-500 bg-red-50 p-4 dark:border-red-500/70 dark:bg-red-950/30">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600 dark:text-red-400">
            Updated 20 September 2026 &mdash; we cut our prices, then cut the price of a reachout 5x
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            <strong>Growth is now $129 a month</strong> (was $149) and <strong>Scale is now
            $279</strong> (was $399). Then, on the same day, the price of one investor reachout fell from
            <strong>$0.98 / $0.645 / $0.465 to $0.196 / $0.172 / $0.164</strong> &mdash; so the plans now
            buy <strong>250 / 750 / 1,700</strong> investor reachouts a month instead of 50 / 200 / 600,
            at the same prices. We also changed what a plan <em>leads with</em>: the <strong>investor
            meetings</strong> it is estimated to produce (est. 6.5 / 19.5 / 44.2 a month), with the
            reachout count kept underneath as how those meetings are produced. Everything marked
            <strong>est.</strong> is an estimate and labelled as one; the plan prices are final.
          </p>
        </div>
        <p>Everything above exists so this part can be boring. Try the slider, then the full price list:</p>

        <BottomLine>
          Free gets you 5 reachouts to test the channel. Below 50/month, Starter ($49) is cheapest per reachout
          among paid tiers; at 200+/month, Growth ($129) beats assembling the DIY stack yourself ($80&ndash;320/mo
          plus a 30&ndash;45 day warm-up that is not optional). The premium buys the compliance gate, verified addresses,
          and warmed infrastructure, not the email itself.
        </BottomLine>

        <OutreachPricingExplorer />
        <div className="overflow-x-auto my-4">
          <table className="min-w-full text-sm border border-slate-200 dark:border-slate-700">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800">
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-left">Price</th>
                <th className="px-4 py-2 text-left">Credits you get / month</th>
                <th className="px-4 py-2 text-left">Included</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Free</td><td className="px-4 py-2.5">$0</td><td className="px-4 py-2.5">980 credits</td><td className="px-4 py-2.5">Your first <strong>5 investor reachouts</strong>: no card required</td></tr>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Starter</td><td className="px-4 py-2.5">$49 / month</td><td className="px-4 py-2.5"><strong>49,000 credits</strong></td><td className="px-4 py-2.5">About <strong>6.5 investor meetings</strong> a month, from 250 investor reachouts sent via <strong>a mailbox and domain we warm for you</strong>, or spend the credits on anything else in the platform</td></tr>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Growth</td><td className="px-4 py-2.5"><span className="font-semibold text-red-600 dark:text-red-400">$129 / month</span> <span className="text-xs text-red-600 dark:text-red-400 line-through">$149</span></td><td className="px-4 py-2.5"><strong>129,000 credits</strong></td><td className="px-4 py-2.5">About <strong>19.5 investor meetings</strong> a month, from 750 investor reachouts sent via <strong>2 mailboxes we warm for you</strong>, or spend the credits on anything else in the platform</td></tr>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Scale</td><td className="px-4 py-2.5"><span className="font-semibold text-red-600 dark:text-red-400">$279 / month</span> <span className="text-xs text-red-600 dark:text-red-400 line-through">$399</span></td><td className="px-4 py-2.5"><strong>279,000 credits</strong></td><td className="px-4 py-2.5">About <strong>44.2 investor meetings</strong> a month, from 1,700 investor reachouts sent via <strong>3 mailboxes we warm for you</strong>, or spend the credits on anything else in the platform: every month, full speed</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>We charge per reachout, not per meeting.</strong> A plan buys <strong>credits</strong>, and
          credits are the platform's currency: <strong>1 USD = 1,000 credits</strong>, and what one
          reachout costs <strong>falls as you move up</strong>: 196 credits on Starter, 172
          on Growth, 164 on Scale — so a bigger plan's credits buy more reachouts. Send, or spend them on anything
          else in the platform: a mailbox-month, a domain, whatever comes next. One currency, one balance,
          every action priced in it.
        </p>
        <p>
          <strong>Our pricing principle: the premium tightens the more you buy.</strong> Starter
          $0.196, Growth $0.172, Scale $0.164 per reachout, every step up buys a better
          unit price. Monthly bills as you go; every plan is month-to-month, cancel anytime.
        </p>
        <p>
          A reachout is one investor reached out to, through any channel, with the address found and
          verified, the compliance check passed for their country, and the send logged. We count reachouts, not
          emails, because you are buying conversations started, not messages fired.
        </p>
        <p className="rounded-lg border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 text-center">
          <strong>No commissions, no equity, no hidden fees &mdash; ever.</strong> One number is published: a{' '}
          <strong>1% success fee</strong>, charged only when your round closes. Agencies take 1&ndash;6% of your
          round <em>plus</em> a $5,000&ndash;25,000 monthly retainer; we charge a flat monthly price, and the 1%
          only once it worked.
        </p>
        <p>
          Every plan includes the components that make the price easy to defend: <strong>The Compliance
          Shield</strong> (a per-country gate on every send and every preview), <strong>Verified-or-Replaced</strong>
          (every address verified at send time; any hard bounce replaced free), <strong>Warm-Up
          Done-For-You</strong> (domains, mailboxes, and the 30&ndash;45 day clock, carried by us), and <strong>The
          Evidence Trail</strong> (every send logged and auditable). And on Growth and Scale there is <strong>The
          First-Conversation Guarantee</strong>: at least one investor response in your first 200 reachouts,
          or we keep sending for free until you get one. We can promise this because the addresses are verified, the
          gate keeps every email on the right side of the rules, and the list is ranked for fit. (The math,
          honestly: the <strong>est.</strong> figures above use the <strong>midpoint</strong> of the tailored
          band — 6.5%. This sentence uses the <strong>floor</strong> of the published cold-outreach band —
          2%, the worst case — and even there, zero replies in 200 verified reachouts is a ~2% event. So
          the guarantee costs us almost nothing and should cost you no sleep.)
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          One honest note on availability: we onboard a limited number of raises per month. Warm infrastructure is
          real, not rented, and per-mailbox capacity is capped by the same compliance rules that protect your domain.
        </p>
        <p>
          Why these numbers: a reachout costs us cents to deliver, and the premium pays for the gate, the verified
          data, the warmed infrastructure, and the effort above. Compare honestly: 200 generic sales contacts a month
          at AiSDR costs $250, our Growth tier does 200 <em>investor</em> reachouts for $129, with the
          fundraising logic and the compliance gate included. A fundraising agency does this work for
          $5,000&ndash;25,000 a month plus a success fee. We think that layer should cost like software:
          three monthly plans, one free tier, and nothing else to compare.
        </p>
        <p>
          <strong>What a month of reachouts gets you, practically.</strong> At Growth pace a month is <strong>750 investor reachouts</strong>, which
          we estimate at <strong>48.8 investor replies</strong> and <strong>19.5 investor meetings</strong>
          &mdash; about <strong>$6.62 per investor meeting</strong>. Carry it for the length of a raise
          (2 to 4 months) and that is 400&ndash;800 reachouts, roughly 10&ndash;20 investor meetings, and a
          realistic shot at term-sheet conversations. Every one of those figures is an
          <strong>estimate</strong>, not a promise. What the plan actually commits to is the pipeline: the
          right investors, reached the right way, every month.
        </p>

        <h3>What a plan is expected to produce &mdash; est.</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>est.</strong> is short for <strong>estimated</strong>. <strong>What you buy is
          credits</strong> (and they spend anywhere in the platform); what one investor reachout deducts
          depends on your plan &mdash; <strong>196</strong> credits on Starter, <strong>172</strong> on
          Growth, <strong>164</strong> on Scale, which is where the volume discount sits. The replies,
          meetings and cost per meeting below are <strong>what we estimate
          you get</strong> for those reachouts &mdash; shown so you can judge the value, never charged.
        </p>
        <div className="overflow-x-auto my-4">
          <table className="min-w-full text-sm border border-slate-200 dark:border-slate-700">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800">
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-left">Credits / month<br />(what you buy)</th>
                <th className="px-4 py-2 text-left">Investor reachouts<br />that buys</th>
                <th className="px-4 py-2 text-left">est. replies<br />/ month</th>
                <th className="px-4 py-2 text-left">est. investor meetings<br />/ month</th>
                <th className="px-4 py-2 text-left">est. cost per<br />investor meeting</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Starter $49</td><td className="px-4 py-2.5"><strong>49,000</strong></td><td className="px-4 py-2.5">250</td><td className="px-4 py-2.5">16.3</td><td className="px-4 py-2.5">6.5</td><td className="px-4 py-2.5 font-semibold">$7.54</td></tr>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Growth <span className="text-red-600 dark:text-red-400">$129</span></td><td className="px-4 py-2.5"><strong>129,000</strong></td><td className="px-4 py-2.5">750</td><td className="px-4 py-2.5">48.8</td><td className="px-4 py-2.5">19.5</td><td className="px-4 py-2.5 font-semibold">$6.62</td></tr>
              <tr className="border-t border-slate-200 dark:border-slate-700"><td className="px-4 py-2.5 font-semibold">Scale <span className="text-red-600 dark:text-red-400">$279</span></td><td className="px-4 py-2.5"><strong>279,000</strong></td><td className="px-4 py-2.5">1,700</td><td className="px-4 py-2.5">110.5</td><td className="px-4 py-2.5">44.2</td><td className="px-4 py-2.5 font-semibold">$6.31</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          How we estimate: <strong>replies = 6.5% of investor reachouts</strong> (the midpoint of the
          3&ndash;10% band published for cold investor outreach with a fit-ranked list and an email written
          for each investor) and <strong>meetings = 40% of replies</strong> (the midpoint of the
          30&ndash;50% reply-to-meeting band). Cost per meeting is the plan price divided by the estimated
          meetings. These are estimates from published research, not promises, and we do not yet have our
          own measured reply rate &mdash; when we do, it replaces these.
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>What makes the reachout count deliverable, and not a claim.</strong> A mailbox can send
          about <strong>25 cold emails a day</strong> before it starts damaging its own reputation, and
          that is the cap our send gate enforces. So each plan comes with the sending infrastructure to
          carry it: <strong>1 mailbox on Starter, 2 on Growth, 3 on Scale</strong> &mdash; domains,
          mailboxes and the warm-up carried by us, not by you. Our published price includes that; the
          tools in the comparison above send from <em>your</em> mailbox, which is why their number is a
          floor and not a total. One honest detail: a brand-new mailbox starts at 5 sends a day and
          ramps over 30&ndash;45 days, so the monthly figure is what a plan delivers <strong>once warm</strong>
          &mdash; and that is also why we onboard a limited number of raises per month.
        </p>

        <MeetingsPerMeetingCostChart />

        <h3>The alternatives, one number per cell &mdash; all outcomes are <strong>est.</strong></h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Read across a row to compare one thing; read down a column to see what one plan actually is.
          Every cell holds a single value &mdash; nothing is bundled. <strong>est.</strong> means
          estimated.
        </p>
        <div className="overflow-x-auto my-4">
          <table className="min-w-full text-sm border border-slate-200 dark:border-slate-700">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800">
                <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 dark:bg-slate-800">Plan</th>
                <th className="px-3 py-2 text-left">Raisy<br />Starter</th>
                <th className="px-3 py-2 text-left">Raisy<br />Growth</th>
                <th className="px-3 py-2 text-left">Raisy<br />Scale</th>
                <th className="px-3 py-2 text-left">Angel Match<br />Basic</th>
                <th className="px-3 py-2 text-left">Angel Match<br />Growth</th>
                <th className="px-3 py-2 text-left">Angel Match<br />Premium</th>
                <th className="px-3 py-2 text-left">OpenVC<br />Premium</th>
                <th className="px-3 py-2 text-left">Metal</th>
                <th className="px-3 py-2 text-left">Full-service<br />agency</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">Price / month</td>
                <td className="px-3 py-2.5"><span className="font-semibold text-red-600 dark:text-red-400">$49</span></td>
                <td className="px-3 py-2.5"><span className="font-semibold text-red-600 dark:text-red-400">$129</span></td>
                <td className="px-3 py-2.5"><span className="font-semibold text-red-600 dark:text-red-400">$279</span></td>
                <td className="px-3 py-2.5">$59</td>
                <td className="px-3 py-2.5">$99</td>
                <td className="px-3 py-2.5">$349</td>
                <td className="px-3 py-2.5">$99</td>
                <td className="px-3 py-2.5">$200 quoted<br /><span className="text-xs">no published price</span></td>
                <td className="px-3 py-2.5">$5,000&ndash;25,000</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">Investor emails you may send / month</td>
                <td className="px-3 py-2.5">250</td>
                <td className="px-3 py-2.5">750</td>
                <td className="px-3 py-2.5">1,700</td>
                <td className="px-3 py-2.5">100</td>
                <td className="px-3 py-2.5">500</td>
                <td className="px-3 py-2.5">2,000</td>
                <td className="px-3 py-2.5">about 150</td>
                <td className="px-3 py-2.5">not published</td>
                <td className="px-3 py-2.5">not published</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">est. replies / month</td>
                <td className="px-3 py-2.5">16.3</td>
                <td className="px-3 py-2.5">48.8</td>
                <td className="px-3 py-2.5">110.5</td>
                <td className="px-3 py-2.5">6.5</td>
                <td className="px-3 py-2.5">32.5</td>
                <td className="px-3 py-2.5">130</td>
                <td className="px-3 py-2.5">9.8</td>
                <td className="px-3 py-2.5">not computable</td>
                <td className="px-3 py-2.5">not computable</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">est. investor meetings / month</td>
                <td className="px-3 py-2.5">6.5</td>
                <td className="px-3 py-2.5">19.5</td>
                <td className="px-3 py-2.5">44.2</td>
                <td className="px-3 py-2.5">2.6</td>
                <td className="px-3 py-2.5">13</td>
                <td className="px-3 py-2.5">52</td>
                <td className="px-3 py-2.5">3.9</td>
                <td className="px-3 py-2.5">not computable</td>
                <td className="px-3 py-2.5">not computable</td>
              </tr>
              <tr className="border-t-2 border-cyan-500/60 dark:border-cyan-400/60">
                <td className="px-3 py-2.5 font-bold sticky left-0 bg-white dark:bg-slate-900">est. cost per investor meeting</td>
                <td className="px-3 py-2.5 font-bold">$7.54</td>
                <td className="px-3 py-2.5 font-bold">$6.62</td>
                <td className="px-3 py-2.5 font-bold">$6.31</td>
                <td className="px-3 py-2.5 font-bold">$23</td>
                <td className="px-3 py-2.5 font-bold">$8</td>
                <td className="px-3 py-2.5 font-bold">$7</td>
                <td className="px-3 py-2.5 font-bold">$25</td>
                <td className="px-3 py-2.5">not computable</td>
                <td className="px-3 py-2.5">not computable</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">Reply rate applied to this column &mdash; the method&rsquo;s, not the vendor&rsquo;s</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">6.5%</td>
                <td className="px-3 py-2.5">&mdash;</td>
                <td className="px-3 py-2.5">&mdash;</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">Who writes each email</td>
                <td className="px-3 py-2.5">we do</td>
                <td className="px-3 py-2.5">we do</td>
                <td className="px-3 py-2.5">we do</td>
                <td className="px-3 py-2.5">you</td>
                <td className="px-3 py-2.5">you</td>
                <td className="px-3 py-2.5">you</td>
                <td className="px-3 py-2.5">you</td>
                <td className="px-3 py-2.5">you</td>
                <td className="px-3 py-2.5">they do</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">Written for each investor, or one template?</td>
                <td className="px-3 py-2.5">for each investor</td>
                <td className="px-3 py-2.5">for each investor</td>
                <td className="px-3 py-2.5">for each investor</td>
                <td className="px-3 py-2.5">template</td>
                <td className="px-3 py-2.5">template</td>
                <td className="px-3 py-2.5">template</td>
                <td className="px-3 py-2.5">template</td>
                <td className="px-3 py-2.5">template</td>
                <td className="px-3 py-2.5">for each investor</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">Sending identity set up and run for you</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">yes</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">Warm-intro paths</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">premium only</td>
                <td className="px-3 py-2.5"><strong>yes</strong></td>
                <td className="px-3 py-2.5">yes</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">Per-country compliance gate</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">yes</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-slate-50 dark:bg-slate-800">Hard bounce replaced free</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">yes</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
                <td className="px-3 py-2.5">no</td>
              </tr>
              <tr className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-white dark:bg-slate-900">Deposit or success fee</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">none</td>
                <td className="px-3 py-2.5">1&ndash;6% of the round</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>Sources and assumptions, so you can check us.</strong> Competitor prices and included
          contact volumes were read from their own pricing pages on <strong>20 September 2026</strong>
          (angelmatch.io/pricing, openvc.app, metal.so/pricing) and may have changed since. Metal
          publishes <em>no</em> price and no sending volume &mdash; it requires a demo &mdash; so only
          the figure quoted to us is shown and the rest is marked not computable, rather than estimated
          from nothing.
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>The reply rate is the same for every column, and that is deliberate.</strong> A reply
          rate belongs to the <em>sending method</em>, not to the vendor: it depends on how well each
          email is written and who it is sent to. An earlier version of this table gave our own columns
          <strong>6.5%</strong> and the database tools <strong>1.5%</strong> inside one comparison
          &mdash; which decided the answer with a number we chose, and priced their lists as though you
          were obliged to send them badly. So one rate now applies to all of us: <strong>6.5%</strong>
          (tailored &mdash; a fit-ranked list and an email written for each investor; the midpoint of the
          3&ndash;10% band published for cold investor outreach), or <strong>1.5%</strong> (generic
          &mdash; a template or bulk send; the published floor for generic cold email). Switch it in the
          chart below and every column moves together. Meetings are estimated at <strong>40% of
          replies</strong> (the midpoint of the 30&ndash;50% reply-to-meeting band), and cost per meeting
          is the plan price divided by those meetings. <strong>Where a plan sends from your own
          domain</strong> &mdash; Angel Match and OpenVC both do &mdash; the mailbox, the domain warm-up
          and the copy stay with you, so their cost per meeting is a <strong>floor</strong> that excludes
          what you must bring yourself; Raisy carries those. All estimates, ours included.
        </p>

        <h2>How long until it works</h2>
        <p>Two honest answers, because there are two different clocks.</p>
        <p>
          <strong>Your first email:</strong> the engine is built and verified today: the funnel, the ranking,
          the compliance gate, and the approval-gated preview all pass their live tests. What remains is connective,
          not inventive: your mailbox connection, a final legal once-over of the first-touch template, and front-end
          polish. That is days of engineering and roughly one to three weeks of calendar, and the longest pole
          is the careful review, not the code.
        </p>
        <p>
          <strong>The complete package</strong>: follow-up sequences that pause the moment an investor replies,
          a warmed inventory of sending infrastructure, verification at scale, per-mailbox health monitoring: the
          engineering runs eight to twelve weeks, with the warm-up clock ticking in parallel whether we code or not.
          Call it a quarter, done properly. We would rather give you a date you can plan a raise around than a date
          that sounds better.
        </p>

        <ChartZoom><OutreachTimelineChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Fundraising is already two jobs: building the company and selling the company. The layer described in this
          post is a third job nobody applies for, and it quietly taxes exactly the founders who move fastest,
          because moving quickly is easier when someone has already swept the path. Folding it into the platform means
          your two scarcest resources, your calendar and your good name, stay where they belong.
        </p>
        <p>
          It also generalizes. The same intent-and-country gate that keeps an investor first touch friendly will keep
          a sales pitch and a support reply on their own right tracks; the same warm-up discipline will serve every
          outbound motion we add later. We built it for the raise, and we built it to last.
        </p>
        <p>
          So the choice in front of you is concrete. Assemble it yourself: $80&ndash;320 a month in tools, a
          30&ndash;45 day warm-up before full volume, and the rules of every recipient&apos;s country on your desk. Or
          open Raisy, where the gate, the verified list, and the timing are already in place, and your five
          first emails are free.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          <strong>What this post does not answer:</strong> deliverability numbers from our own live sends (the gate
          is verified; production placement data comes with the first real campaigns), and the legal sign-off itself
         . We build the gate; counsel owns the judgment. Pricing is no longer an open question, see the
          table above.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team/entities" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Start your raise with the groundwork already laid, create your company profile at raisy.team/entities and see your ranked investor list before you send a word
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is it okay to email investors I haven\'t met?', a: 'Yes, introducing yourself to investors is legal in every jurisdiction we researched. What varies by country is the how, and that is exactly what the gate handles for you: the right form for the US, consent-first care for countries like Germany, a documented basis for Canada, and extra care for investment-related messages into the UK.' },
      { q: 'Why not just use a sending tool and an inexpensive list?', a: 'You can, and many do, the stack runs $80–320 a month plus a month of warm-up. What those tools leave with you is the judgment: which rules apply to which recipient, whether the list is still fresh, and how your sending reputation is holding up. We built Raisy so that judgment comes with the product.' },
      { q: 'Why five free emails and not fifty?', a: 'Five is enough to feel the channel working for your raise, and at small volumes every email carries more weight, so we would rather your first five be excellent than your first fifty be average.' },
      { q: 'Do you send from your servers or my mailbox?', a: 'Always your mailbox, your name, your call. Raisy prepares, verifies, checks, and records; nothing leaves without your approval.' },
      { q: 'Why do you count "reachouts" and not emails?', a: 'Because you are buying conversations started, not messages fired. One reachout is one investor reached out to, through any channel: with the address found and verified, the compliance check passed for their country, and the send logged. A hard-bounced reachout is replaced free.' },
    ],
    sources:
      'FTC, "CAN-SPAM Act: A Compliance Guide for Business" (penalty figure current for 2026); Google, "Email sender guidelines" (complaint-rate thresholds and bulk-sender classification); Spamhaus, new-domain flagging observations (~30-day window); CRTC, Canada\'s Anti-Spam Legislation guidance; legislation.gov.uk, Financial Services and Markets Act 2000 s.21 and UK DUAA PECR uplift (in force 2026-02-05); U.S. SEC, Rule 506(b)/(c) general-solicitation framework; M3AAWG best-practice publications on warm-up and subdomain reputation; practitioner pricing and fleet norms compiled in Raisy\'s outreach research corpus, 2026-08 (all figures in raisy_outreach_infra_costs.csv); Raisy engineering verification runs, 2026-08-23 (18/18 gate unit tests; 6/6 live API QA capture of the funnel, ranking, and gated preview endpoints).',
  },
  {
    slug: 'the-investor-outreach-execution-challenge',
    title: 'AI Investor Outreach: From URL to Sent Message',
    date: '2026-08-17',
    category: 'Product',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/the-investor-outreach-execution-challenge.jpg',
    excerpt:
      'Investor databases give you lists. AI SDRs send generic email. Raisy runs the fundraising outreach flow from URL to sent message, with a human gate on every send.',
    keywords: ['AI investor outreach', 'fundraising outreach automation', 'investor outreach tool', 'AI fundraising assistant', 'startup fundraising automation'],
    content: (
      <>
        <p>
          You have raised before, or you are about to. The first thing every founder learns is that fundraising is
          two jobs: building the company, and selling the company. The second job is where most rounds stall,
          not because the idea is weak, but because the outreach operation is brittle, slow, and expensive.
        </p>

        <h2>What we set out to change</h2>
        <p>
          The current tools split the work in half. On one side, investor databases and fundraising CRMs give you
          names, firms, and stages. They are useful, but they stop at the list. You still research each investor,
          draft each email, track each reply, and manage the follow-up yourself. On the other side, generic AI SDRs
          will send email at scale, but they are built for SaaS sales, not a raise. They do not know the difference
          between a pre-seed angel and a Series B growth fund, and they do not adapt tone to the relationship stage.
        </p>
        <p>
          The result is a squeeze. Founders either pay a retainer plus success fee to a fundraising advisor, hire an SDR
          whose cost sits beyond their budget, or spend their own CEO hours doing work that scales poorly. The common thread: the tool
          informs, but the founder executes.
        </p>

        <OutreachCompetitionMatrix />

        <BottomLine>
          Investor databases (Crunchbase, PitchBook) execute nothing. They hand you a list. Generic cold-outreach
          tools (Apollo, Instantly) execute sends but are not built for a raise. Raisy is the only option building at
          the intersection: fundraising-specific AND execution, not just information.
        </BottomLine>

        <p>
          The matrix above places the main options on two axes: how much of the outreach they execute (from
          &quot;gives data&quot; to &quot;sends outreach&quot;) and how specific they are to fundraising (from generic
          horizontal tools to raise-vertical products). The cluster in the top-left is the fundraising-intelligence
          camp: good lists, no send. The cluster in the bottom-right is the generic-execution camp. They
          send, but not for a raise. The top-right quadrant, where execution and fundraising specificity meet, is
          where Raisy is building.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Competitor positions are Raisy&apos;s own documented assessment of each tool&apos;s current public
          positioning, not an independent third-party benchmark, see the Sources footer.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          Raisy treats investor outreach as a single, repeatable process inside the company operating system: paste a
          URL, confirm the entity profile, run the pre-built Investor Outreach process, review the ranked investor
          list and the drafted emails, and approve the sends. The bot does the research, enrichment, ranking, and
          drafting. You stay at the decision gate, exactly the same{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            humans decide, bots prepare
          </Link>{' '}
          rule that governs every other Raisy process.
        </p>
        <p>
          This is not a generic SDR with a fundraising template. The process is built from the shape of a raise:
          stage-matched investors, geography and ticket-size preferences, relationship-stage tracking, and email asks
          that fit the moment: advice, meeting, or both. Every send waits for your approval, and every approval
          or rejection teaches the system how this company wants to sound.
        </p>

        <h2>How it works (the technical layer)</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Entity from URL.</strong> Raisy reads the homepage and builds a structured company profile: what you do, who you serve, stage, and traction signals.</li>
          <li><strong>Pre-built process.</strong> The Investor Outreach process is a global template, not a blank workflow. It knows the steps of a raise and chains the right bots.</li>
          <li><strong>Preference wizard, pre-filled.</strong> Stage, geography, and ticket-size assumptions are inferred from the entity and shown to you. You can accept them or adjust them.</li>
          <li><strong>Ranked investor list.</strong> A fit-scoring bot ranks a broad investor database and preselects the most feasible matches for your profile, with reasoning.</li>
          <li><strong>Email drafting from templates.</strong> Emails are assembled from raise-specific ask templates (advice, meeting, update, follow-up) plus the context for each investor.</li>
          <li><strong>Human edit and diagnose.</strong> A centralized revision panel gives you review, edit, diagnose, and compare, the same learning loop Raisy uses for every bot.</li>
          <li><strong>Subdomain and sender provisioned.</strong> Your own subdomain and sender are prepared in the background so the first emails go from your address, not a shared pool.</li>
          <li><strong>Approval before send.</strong> Nothing leaves without your sign-off. Once approved, the send fires and the relationship stage updates automatically.</li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          You paste your homepage. Within one minute you see a ranked list of investors, drafted emails for the top
          matches, and a send button that is gated behind your approval. You review one email, change the ask from
          &quot;meeting&quot; to &quot;advice&quot; if you want, and click approve. The first five sends are free;
          after that you pay per investor. You can see the relationship stage move from &quot;sent&quot; to
          &quot;opened&quot; to &quot;replied&quot; without opening a spreadsheet.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          The execution challenge is not only a UX question. It is the reason most founders under-raise or burn months on a
          round. A list without execution is a research task. Generic execution without fundraising context is a
          reputation risk. Raisy combines the two so the founder can focus on the conversations that matter, while
          the system handles the toil that currently eats the CEO&apos;s calendar.
        </p>
        <p>
          The same process model also scales to sales, recruiting, support, investor relations, debt, PR, and
          partnerships later. The investor-outreach beachhead is narrow by design: ship one complete use case, prove
          it on our own raise, then expand. (See how a department gets built in{' '}
          <Link to="/blog/from-url-to-department-in-one-paste" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            from URL to department in one paste
          </Link>
          , and browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Close the execution challenge, paste your URL and send your first investor outreach from raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Are you saying Raisy replaces a fundraising advisor?', a: 'No. Raisy replaces the repetitive parts of outreach: list building, enrichment, drafting, and sending, so the founder or advisor can focus on relationship and negotiation.' },
      { q: 'How is this different from a generic AI SDR?', a: 'Generic AI SDRs execute outbound for any sales motion. Raisy executes a raise-specific process: investor ranking, stage-aware templates, and a human gate on every send.' },
      { q: 'What about investor databases?', a: 'They are strong at information and pipeline management, but they do not draft or send. Raisy uses a similar data layer and then runs the outreach.' },
      { q: 'Is every email really approved by a human?', a: 'Yes. The default mode is human-in-the-loop. A bot can earn more autonomy over time through a transparent confidence score, but an external send always starts behind a gate.' },
      { q: 'What is the "under one minute" claim based on?', a: 'It is the design target for the first-time user journey from URL paste to first approved send, measured on the happy path. Real-world timing depends on how much the user edits.' },
    ],
    sources:
      'Raisy internal competitive assessment (2026-08-11) of each tool’s current public positioning: presented as documented assessment, not independent benchmark data. Public reference: Amplemarket, "Best AI Sales Agents 2026" (amplemarket.com/blog/best-ai-sales-agents). What this post does not answer: it does not yet publish live dogfooding metrics (time-to-first-send, reply rate, meetings booked); those will follow once Raisy’s own raise is run on the product and disclosed in a separate post.',
  },
  {
    slug: 'raisy-vs-prompt-engineering-factorial',
    title: 'Prompt Engineering 190 vs Agent Memory 238',
    date: '2026-08-14',
    category: 'Research',
    series: 'Ceteris Paribus Benchmarks',
    audience: { persona: 'BotBuilder', background: 'Professional', depth: 'DeepDive' },
    image: '/blog/raisy-vs-prompt-engineering-factorial.jpg',
    excerpt:
      'Four prompt-engineering strategies averaged 190 on a 290-point rubric. Structured memory scored 238 on the same task, same model, same judge. The full run, including what it does not prove.',
    keywords: [
      'AI agent benchmark',
      'prompt engineering vs agent memory',
      'ceteris paribus LLM benchmark',
      'kernel assembly',
      'LLM-as-judge',
    ],
    content: (
      <>
        <p>
          Everyone tuning an LLM reaches the same fork. You can keep rewriting the system prompt, or you can give the
          model a memory: a structured, versioned set of rules and context assembled for it automatically.
        </p>
        <p>
          We tested both on the same task, the same model, and the same judge. Four prompt-engineering strategies
          averaged <strong>190</strong> on a 290-point rubric. Raisy Memory scored <strong>238</strong>.
        </p>

        <h2>The setup</h2>
        <p>
          The task: draft a physical resignation letter: plain text, printable. Deliberately not an email, so the model
          does not fall back on its email-shaped defaults.
        </p>
        <p>
          Five arms. Four vary the system prompt given to a plain model call: none at all, then one-sentence,
          &quot;extensive&quot;, and &quot;professional&quot; prompts generated from the same meta-prompt with only the
          adjective changed. The fifth replaces the prompt entirely with Raisy&apos;s assembled memory: purpose,
          objective, capability, tone, and output contract, loaded and versioned automatically. (Why a rule a bot knows
          is a versioned artifact rather than prompt text:{' '}
          <Link to="/blog/a-kernel-is-a-contract" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            a kernel is a contract
          </Link>
          .)
        </p>
        <p>
          Everything else is held fixed: same model (gemini-2.5-flash-lite), same temperature (0.1), same judge, same
          29-dimension rubric, same context, same task text. Only the instruction layer changes.
        </p>

        <p>
          Here is exactly what each arm was told. The four prompt arms are shown verbatim. The Raisy Memory tab shows
          the kernel <em>coordinates</em> it loaded and whether each was reused from the shared library or authored for
          this bot, the same preview the create-bot flow shows before it builds. Kernel content is not published.
        </p>

        <InstructionLayerTabs />

        <h2>The result</h2>

        <ChartZoom><PromptVsMemoryChart /></ChartZoom>

        <p>
          The more interesting finding is not the difference. It is the flatness on the left of the chart. Writing{' '}
          <em>no</em> system prompt scored 191. Writing an <em>extensive</em> one scored 180. A
          &quot;professional&quot; one scored 195. Four different amounts of prompt-engineering effort produced a
          15-point band with no ordering, and the worst score belongs to the most elaborate prompt.
        </p>
        <p>
          That is the practical case for memory over prompting. Prompt engineering on a capable model is not a dial you
          can turn up. Adding words to a system prompt competes with the task rather than reinforcing it, and there is no
          reliable direction of improvement to iterate along. A structured memory changes what the model{' '}
          <em>knows</em> going in, not how loudly it is instructed, and unlike a prompt, it is versioned, shared across
          departments, and auditable per call.
        </p>

        <h2>Read the letters yourself</h2>
        <p>
          Scores are a proxy. The letters are the actual product, so here they are unedited, switch between the arms
          and judge the writing directly. It is worth noticing how similar the four prompt-engineered letters are to each
          other, and how little the extra instruction text changed what came out.
        </p>

        <LetterOutputTabs />

        <h2>Where the difference actually lands</h2>
        <p>
          A single total hides which qualities moved, and the breakdown is not flattering in every direction. Raisy
          Memory scores a perfect 10 on the dimensions that depend on knowing the situation, specificity (10 vs 4.0
          average), no omissions (10 vs 4.5), context usage (10 vs 5.0), completeness (10 vs 5.2). That is what a memory
          is for: it carried the tenure, the team, and the relationship into the letter, where the prompt arms wrote
          around challenges they had not yet measured.
        </p>
        <p>
          It also <em>loses</em> badly on three: format compliance (1 vs 10), instruction adherence (1 vs 7.8), and task
          completion (5 vs 7.0). The judge penalised the Raisy arm for returning its letter inside a JSON envelope, a
          real defect for this comparison, and one the totals would have hidden. The 48-point lead survives despite it,
          not because of it.
        </p>

        <ChartZoom><JudgeHeatmapChart /></ChartZoom>

        <h2>What this does not prove</h2>
        <p>
          Every number above is a single sample. The null hypothesis, that Raisy Memory and the prompt-engineered arms
          perform identically and the 48-point difference is chance, is <strong>not rejected by this data</strong>. In plain
          words: we ran each arm once, and repeated runs of an unchanged configuration on this task have varied by as
          much as 76 points, so a single 48-point spread could be luck. It is a hypothesis for the next run, not a proven
          result. Publication-grade numbers need at least three runs per arm with a reported confidence interval.
        </p>
        <p>
          It is also one task on one model. A resignation letter is not a classification, an extraction, or a code
          change, and gemini-2.5-flash-lite is not every model. Two of the 29 rubric dimensions,{' '}
          <code>decision_quality</code> and <code>reasoning_soundness</code>, do not fit a writing task and returned
          degenerate scores on defect-free output; they remain in the totals rather than being quietly dropped.
        </p>

        <h2>Reproducibility</h2>
        <p>
          One command reruns the experiment. The runner holds model, temperature, and judge identical across both arms
          and prints a parameter audit before every run, so the comparison is checkable rather than asserted,{' '}
          <Link to="/blog/every-action-leaves-a-receipt" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            every action leaves a receipt
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink> for the rest of the series.
        </p>

        <h2>Conclusion</h2>

        <BottomLine>
          Structured memory beat the best prompt-engineering strategy by 48 points (17% of the rubric) on this task,
          but it is a single run, not a proven result: repeated runs of an unchanged config varied by up to 76 points,
          so treat this as a hypothesis to replicate on your own task, not a settled verdict.
        </BottomLine>

        <p>
          Four strategies for writing a better system prompt landed within 15 points of each other, in no particular
          order, and the most elaborate one scored lowest. That is what prompt iteration bought on this task: noise.
          Structured memory scored 48 points higher, 17% of the rubric: on the same model, the same temperature, and
          the same judge.
        </p>
        <p>
          If you are still improving output by rewriting prompt wording, that 48-point difference is the distance you are trying
          to close by hand, one edit at a time, with no gradient to follow. A memory closes it by changing what the bot
          knows rather than how it is told. Build one and run this comparison against your own task, the numbers above
          are one letter, on one model, and your task is not ours.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team/agent-hub" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Build a bot with memory in the Agent Hub
          </a>
        </p>
      </>
    ),
    faq: [
      {
        q: 'Why does no system prompt beat an extensive one?',
        a: 'On a task a capable model already knows how to do, extra instruction text competes with the task rather than reinforcing it. The four prompt arms span 180–195 with no ordering, which is the point: prompt effort is not a dial you can reliably turn up.',
      },
      {
        q: 'Is a 48-point difference statistically significant?',
        a: 'No, and the post says so. Each arm is a single sample, and repeated runs of an unchanged configuration varied by up to 76 points on this task. The difference is a hypothesis for a replicated run, not a proven result. Publication needs at least three runs per arm.',
      },
      {
        q: 'How is this a fair comparison?',
        a: 'Both arms use the same model, temperature, judge, rubric, context, and task text; only the instruction layer differs. The runner prints a parameter audit before every run so the constants are checkable rather than asserted.',
      },
      {
        q: 'What exactly is Raisy Memory?',
        a: 'The bot’s assembled kernels: purpose, objective, capability, tone, and output contract: loaded and versioned automatically at call time. Unlike prompt text, each is an immutable version that can be audited, shared across departments, and rolled back.',
      },
    ],
    sources:
      'Data: raisy_BE/reports/benchmark/benchmark_factorial_local_20260813_100923.json (run 2026-08-13). Runner: scripts/run_factorial_benchmark_local.py --cfg cfg/benchmark_factorial_tasks_letter.json. Judge: cfg/judge_system_prompt.md, 29 dimensions, gemini-3.1-pro-preview. Both arms: gemini-2.5-flash-lite at temperature 0.1. Single sample per arm: see "What this does not prove".',
  },
  {
    slug: 'the-ai-reliability-challenge-in-numbers',
    title: 'AI Agent Reliability: Hallucinating 3-12%',
    date: '2026-08-13',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'OpsLead', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/the-ai-reliability-challenge-in-numbers.jpg',
    excerpt:
      'AI agents hallucinate 3–12% of the time, fall short on most office tasks, and forget what you teach them. Here are the numbers, and what governed autonomy does about it.',
    keywords: ['AI agent reliability', 'AI hallucination rate', 'AI agent failure rate', 'AI agent memory loss', 'AI agent production failure'],
    content: (
      <>
        <p>
          The most expensive mistake in AI right now is not the model you choose. It is trusting any of them, on their
          own, with real work. These weaknesses are not hypotheticals in a benchmark lab. They are measured,
          published, and worsening. This post collects the hard numbers so you can make an honest decision about where
          AI is safe to run today, and where it is not.
        </p>

        <h2>Quality, models are unreliable</h2>
        <p>
          Hallucination is a baseline property, not an edge case. On the simplest possible grounding task, summarize a
          document using only its facts, current frontier models invent content 3–12% of the time. The best measured
          model sits at 1.8%, and the heavy reasoning models are dramatically worse.
        </p>

        <ChartZoom><HallucinationRateChart /></ChartZoom>

        <p>
          The spread is huge: from GPT-5.4-nano at 3.1% to o3-pro at 23.3%. There is no monotonic improvement, newer
          versions of the same model line swing between 10.9% and 12.2%, so the model you approved last quarter may not
          behave the same way this quarter.
        </p>

        <h2>Capability: agents fall short on most real work</h2>
        <p>
          Benchmark scores overstate what agents do in production. On a 1,000-task benchmark of real office work,
          top-performing models from Anthropic, OpenAI, Google, and Amazon completed between 1.7% and 24% of tasks, with
          most well under 35%, an aggregate success of roughly 14.4%. Roughly 94% of agent deployments fall short of delivering
          their promised ROI in production despite strong benchmark scores. The distance between a benchmark and a production
          run is the difference between a model knowing how and a model reliably doing.
        </p>

        <ChartZoom><ReliabilityGapChart /></ChartZoom>

        <h2>Memory, AI forgets what you teach it</h2>
        <p>
          Across sustained interactions, commercial assistants and long-context LLMs lose roughly 30% accuracy at
          recalling what they were told (LongMemEval, 500 questions, five memory abilities). Within a single long
          context, models perform best when the relevant information sits at the start or end and degrade sharply in the
          middle, even for models marketed as long-context (Lost in the Middle).
        </p>

        <ChartZoom><MemoryDecayChart /></ChartZoom>

        <p>
          For a single assistant, forgetting a third of what you taught it is annoying. For a workforce it is fatal: a
          lesson learned by Sales is never known by Recruiting unless the memory is shared, versioned, and auditable
          across departments. No single-function tool provides that.
        </p>

        <h2>Cost and velocity, the model you pick is temporary</h2>
        <p>
          Per-task cost varies by more than 100× depending on the model, from fractions of a cent to over a dollar.
          Inference prices collapsed roughly 280× in two years, and hardware cost keeps falling about 30% a year. Over
          600 models from roughly 58 providers are available today, and the spread between the top and the 10th-ranked
          model shrank from 11.9% to 5.4% in a single year. The playbook, process, and approval rules around a model are
          the only durable assets.
        </p>

        <h2>Trust, the market is being burned</h2>
        <p>
          A flagship &quot;run the whole company&quot; agent sits at 1.8/5 on Trustpilot with thousands of customers. A
          major AI-SDR vendor previously claimed customers it did not have. Trust, not capability, is the battleground.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          The pattern across all five weaknesses is the same: reliability is a system property, not a model property.
          It comes from what you put around the model. Raisy is built for that. Every external action passes a human
          gate until a bot earns autonomy through a transparent confidence score; kernels are versioned, cross-session,
          and cross-department, so a lesson learned by Fundraising is known by Sales, Recruiting, and Support; and every
          bot call, process run, approval, and promotion is logged,{' '}
          <Link to="/blog/every-action-leaves-a-receipt" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            every action leaves a receipt
          </Link>
          . Models are a routing decision, not an architecture: when the frontier moves, Raisy routes to the cheapest
          model that passes the eval. (See how trust is earned in{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            a bot&apos;s career path
          </Link>
          , why{' '}
          <Link to="/blog/a-kernel-is-a-contract" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            a kernel is a contract
          </Link>
          , and the human-first rule in{' '}
          <Link to="/blog/humans-decide-bots-prepare" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            humans decide, bots prepare
          </Link>
          . Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Every number in this post is a tax an ungoverned AI deployment pays: hallucination taxes trust, unfinished work
          taxes output, memory loss taxes learning, model churn taxes rework, and trust collapse taxes adoption. Raisy&apos;s
          answer is not a better model. It is the governance that lets you run today&apos;s models and tomorrow&apos;s safely, with
          your people at every decision gate.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Put governance around your AI: see a workforce that remembers, gates, and logs at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is the claim that AI outputs are never trustworthy?', a: 'No. They are excellent at prepared, reviewed, and gated work. The evidence says they are unreliable at autonomous, ungated work: the difference this post quantifies.' },
      { q: 'Why does a 3–12% hallucination rate matter if I review everything?', a: 'Because review is only meaningful if it is easy to reconstruct what happened. A gate without a receipt is a guess. Raisy pairs both.' },
      { q: 'Does memory really degrade that much?', a: 'The 30% drop is measured across sustained interactions on a 500-question benchmark. Raisy\u2019s answer is versioned, shared memory: a correction stored once is known by every department.' },
      { q: 'What if a cheaper model comes out?', a: 'Raisy treats that as a feature. Kernels are model-agnostic and evals route to the cheapest model that passes the quality bar, so a new release improves cost and capability without rework.' },
    ],
    sources:
      'Vectara Hallucination Leaderboard (HHEM-2.3, updated 2026-05-11), https://github.com/vectara/hallucination-leaderboard; LongMemEval (ICLR 2025), https://arxiv.org/abs/2410.10813; Lost in the Middle (TACL 2023), https://arxiv.org/abs/2307.03172; Stanford HAI AI Index Report 2025, https://hai.stanford.edu/ai-index/2025-ai-index-report; Carnegie Mellon + Salesforce agent studies (via mlq.ai and The Register), https://mlq.ai/news/carnegie-mellon-study-finds-ai-agents-fail-at-office-tasks-nearly-70-of-the-time/; Fiddler, "AI Agent Failure Rate: Why 70–95% Fail in Production": https://www.fiddler.ai/blog/ai-agent-failure-rate; agentmarketcap.ai, "The 94% AI Production Failure Rate vs 80%+ Benchmark Scores" (2026-04-14), https://agentmarketcap.ai/blog/2026/04/14/ai-agent-94-percent-production-failure-rate-vs-benchmark-scores; artificialanalysis.ai (Aug 2026), https://artificialanalysis.ai/; Raisy reference data in industryProblems.json. Live verification of Raisy bots against these baselines is pending a public benchmark API and will be disclosed in a follow-up post rather than asserted here.',
  },
  {
    slug: 'humans-decide-bots-prepare',
    title: 'Human-in-the-Loop AI: Bots Prepare, Humans Decide',
    date: '2026-08-06',
    category: 'Vision',
    series: 'The Raisy Blog',
    audience: { persona: 'OpsLead', background: 'Beginner', depth: 'Overview' },
    image: '/blog/humans-decide-bots-prepare.jpg',
    excerpt:
      'The question is not whether AI replaces you. It is whether you can review what it prepares. Raisy\u2019s rule: bots prepare, humans gate every external action.',
    keywords: ['human-in-the-loop AI', 'human oversight AI agents', 'AI prepares human decides', 'AI governance', 'trustworthy AI workflow'],
    content: (
      <>
        <p>
          The question people ask about AI is whether it replaces you. That is the wrong question. The real question is
          whether you can review what it prepares. A tool that drafts, sources, and tracks for you, and stops the
          moment a prepared action would cross to the outside world, does not replace you. It gives you more room to
          decide.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Two extremes dominate how companies use AI today. In the first, nothing is automated, so a person does every
          draft, every search, every follow-up by hand. In the second, an agent acts on its own with nobody able to see
          what it did or why. The first wastes people on toil; the second asks people to trust a black box.
        </p>

        <h2>The stance / how Raisy fixes it</h2>
        <p>
          Raisy separates the work an AI does well from the decision only a person should make. A bot prepares: it
          drafts the email, researches the investor, screens the candidate, tracks the follow-up. A human decides:
          approve, edit, or reject: at the moment the prepared work would cross a boundary. That moment is a gate, and
          the gate is the point.
        </p>
        <p>
          Autonomy is earned, not assumed. A bot starts in <strong>MANUAL</strong> mode, where every prepared action
          waits for you. As its confidence grows from your approvals, you promote it through <strong>SEMI_AUTO</strong>{' '}
          to <strong>FULL_AUTO</strong>, where it runs and you audit anytime. (That lifecycle is{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            a career path
          </Link>{' '}
          of its own, and the{' '}
          <Link to="/blog/every-action-leaves-a-receipt" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            audit trail
          </Link>{' '}
          is what makes review real. Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>How it works</h2>
        <ul>
          <li><strong>AI prepares.</strong> Each bot drafts, researches, and tracks inside its process, using its kernels, the rules it knows.</li>
          <li><strong>Humans gate.</strong> A run that would cross to the outside world pauses in review mode until a person approves, edits, or rejects.</li>
          <li><strong>Trust is earned per bot.</strong> Approval mode is a per-bot setting; autonomy rises with a confidence score you control.</li>
          <li><strong>Everything is logged.</strong> Every preparation, every gate decision, every promotion is an immutable, dated row.</li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          You see a roster of bots, each with its role, its approval mode, and its confidence. A bot prepares an
          investor update; you read it, edit a line, approve. The bot&apos;s confidence ticks up. Later, you promote it
          to SEMI_AUTO; routine actions proceed, but the first outbound email still pauses for you. You never choose
          between &quot;unreviewed AI&quot; and &quot;AI that never does anything&quot;, the review is the product.
        </p>

        <ChartZoom><AutonomyLadderChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Automation exists to create room for human judgment, not to erase it. When a machine drafts and a person
          decides, a young company can afford to attempt functions that used to require a hire, without giving up the
          ability to say no. The human is the final decision-maker, and the system is built so that stays true at scale.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            See the gate for yourself, run one bot at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Does Raisy replace my team?', a: 'No. Bots do the preparing; people make the decisions. ROI comparisons price workloads and functions, never people.' },
      { q: 'Can a bot act without my review?', a: 'Only after you promote it to FULL_AUTO, and even then every action is logged for you to audit. Nothing runs unrecorded.' },
      { q: 'How do I know what a bot prepared?', a: 'Every preparation and every run writes a dated, immutable log row you can reconstruct.' },
      { q: 'Is autonomy permanent?', a: 'No. You can demote a bot at any time; trust is a per-bot setting, not a one-way door.' },
      { q: 'What is the one rule to remember?', a: 'AI prepares. Human review is the gate. Oversight scales with trust and never disappears.' },
    ],
    sources:
      'Raisy System Manifest (MR0: AI prepares, human review is the execution gate; MR0a: oversight scales with trust, never disappears). Raisy Human Manifesto (AI-Assisted, Human-Directed; the human is the final decision-maker). Live verification pending a public API: logged as a setup task before publication.',
  },
  {
    slug: 'from-url-to-department-in-one-paste',
    title: 'Website to AI Company: Paste a URL, Get a Department',
    date: '2026-08-06',
    category: 'Product',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Beginner', depth: 'HowTo' },
    image: '/blog/from-url-to-department-in-one-paste.jpg',
    excerpt:
      'Paste a website URL and a department shows up. The step-by-step path from your public presence to proposed bots and deployed processes.',
    keywords: ['AI company setup from URL', 'AI workforce onboarding', 'website to AI company', 'no-code AI agents', 'bot deployment walkthrough'],
    content: (
      <>
        <p>
          Paste a website URL and a department shows up. That is the claim, and it is a specific path, not a slogan:
          URL, company profile, proposed departments and bots, deployed processes, your review. This post walks the path
          step by step so you know what happens between the paste and the first proposal, and where a human&apos;s
          judgment enters.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Setting up an AI assistant for a company usually means configuration: pick tools, write instructions, map
          workflows, connect data. That work is why most companies never finish. The alternative, a blank chat that
          &quot;just answers questions&quot;, never does anything on its own. What completes the picture is a bridge from a
          company&apos;s public identity to a running, reviewed set of bots.
        </p>

        <h2>The path / how Raisy does it</h2>
        <ol className="list-decimal pl-5 space-y-2">
          <li><strong>You paste a URL.</strong> A company website or LinkedIn page is enough. No configuration, no training data upload.</li>
          <li><strong>Raisy reads the public presence.</strong> It extracts brand voice, value propositions, products, personas, and differentiators into a structured company profile.</li>
          <li><strong>Raisy proposes departments and bots.</strong> For each relevant function it proposes bot roles and the processes they would run.</li>
          <li><strong>You review.</strong> The dashboard shows every proposed bot and process. You approve, edit, or reject before anything is deployed.</li>
          <li><strong>Bots earn autonomy.</strong> As you approve runs, each bot&apos;s confidence grows and it can be promoted from MANUAL to SEMI_AUTO to FULL_AUTO, at its own pace.</li>
        </ol>
        <p>
          Every step of that path is logged, so the setup itself is auditable. This is the{' '}
          <Link to="/blog/ai-company-operating-system" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            company operating system
          </Link>{' '}
          in practice, and the{' '}
          <Link to="/blog/ai-workforce-roi" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            cost model
          </Link>{' '}
          explains what the resulting department is worth. Browse the <BlogHubLink>blog</BlogHubLink>.
        </p>

        <h2>What the user actually sees</h2>
        <p>
          You paste yourcompany.com. Minutes later you have a company profile, a list of proposed bot roles per
          department, and drafted processes behind them. You review the first outputs, promote the bots that earn it,
          and watch the log fill with every action: searchable, dated, and attributable. The setup does not ask you to
          configure anything first; it asks you to review what it prepared.
        </p>

        <ChartZoom><UrlToCompanyChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          The bridge from identity to operation is what turns an AI platform from a chat into a workforce. When setup is
          a paste and a review instead of a project, a young company can afford to attempt functions that used to
          require a hire. And because the human reviews before deployment and at every external action, the speed never
          comes at the cost of judgment. Bots prepare; people decide.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Paste your company URL at raisy.team and watch the proposals arrive
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'What does Raisy need from me to start?', a: 'A public URL. It reads your public presence and proposes what it finds; you review before anything deploys.' },
      { q: 'Do I have to configure the bots?', a: 'No. The first proposals are drafts you can edit. Configuration is replaced by review.' },
      { q: 'Can I change a proposal?', a: 'Yes. You approve, edit, or reject each proposed bot and process before it activates.' },
      { q: 'How long does setup take?', a: 'From paste to first proposals in minutes; from there, autonomy is earned at each bot\u2019s own pace.' },
      { q: 'What if the URL is not a company site?', a: 'A LinkedIn page or a product page works too; the profile is built from whatever public presence you provide.' },
    ],
    sources:
      'Raisy System Manifest (MR0 / MR0a: creation is AI-prepared and reviewable, not gated; MR0b top-down design). Live verification of the ingestion run pending a public API: logged as a setup task before publication.',
  },
  {
    slug: 'a-process-is-a-graph-with-approval-gates',
    title: 'AI Process Orchestration: Why Prose Playbooks Break',
    date: '2026-08-06',
    category: 'Engineering',
    series: 'The Raisy Blog',
    audience: { persona: 'BotBuilder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/a-process-is-a-graph-with-approval-gates.jpg',
    excerpt:
      'A playbook in prose is not auditable. A process written as a directed graph of bot steps with approval gates is. How Raisy makes repeatable methods reviewable.',
    keywords: ['AI process orchestration', 'approval workflows AI', 'process automation graph', 'AI playbook', 'audit-ready workflows'],
    content: (
      <>
        <p>
          A playbook written in prose is not auditable. There is no way to point to step four and say &quot;this is exactly
          what ran, in this order, and here is where it stopped for a human.&quot; A process written as a directed graph
          of steps: each step executed by a bot, some edges gated for human approval, can.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Companies run on repeatable methods: qualify a lead, prepare an investor update, screen a candidate. In most
          tools those methods exist as prose: a doc, an email thread, a person&apos;s memory. Prose falls short in two
          ways. It does not execute, so a person still does every step by hand. And it is not auditable, because
          nothing records which steps ran, in what order, or where a decision was made.
        </p>
        <p>
          The second is the boundary. The risky part of automation is not the drafting; it is the moment a
          prepared action crosses to the outside world. A playbook gives no natural place to pause there. (A process
          that runs and crosses a boundary is exactly the event{' '}
          <Link to="/blog/every-action-leaves-a-receipt" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            every action&apos;s receipt
          </Link>{' '}
          records, and the steps load{' '}
          <Link to="/blog/a-kernel-is-a-contract" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            kernels
          </Link>{' '}
         , the rules each bot applies. Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          A process is a directed graph: nodes are steps, edges are order, and some edges carry an approval gate. Each
          step is executed by a bot. When a run reaches a gate on an edge that leads outside the company, it pauses and
          enters review mode: a human approves, edits, or rejects the prepared action before it proceeds. The graph is
          the source of truth; the run is a dated execution of that graph.
        </p>

        <h2>How it works</h2>
        <ul>
          <li><strong>Steps are nodes.</strong> Each step names the bot that executes it and the kernels that bot loads.</li>
          <li><strong>Order is edges.</strong> Directed edges define sequence and branches; a run follows the graph, not a memory.</li>
          <li><strong>Gates pause external effects.</strong> An edge that would cross to the outside world carries a gate; the run stops in review mode until a human decides.</li>
          <li><strong>Approval modes scale.</strong> A bot in MANUAL is gated on every action; in SEMI_AUTO it passes routine actions and gates the external ones; in FULL_AUTO it runs and you audit anytime.</li>
          <li><strong>Every run is logged.</strong> The process run record captures the graph, the order that actually executed, and where it paused.</li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          You open a process and see its graph: four steps, one branch, one gate before the final email step. A run
          starts, steps one through three complete, the run pauses at the gate with the prepared email and a prompt:
          approve, edit, or reject. You approve, the email sends, and the run is recorded end to end. The same graph
          runs the same method tomorrow, and you can point to where your judgment entered.
        </p>

        <ChartZoom><ProcessGraphChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Structure is what makes automation reviewable. The moment a method becomes a graph, three things change: it
          executes without a person doing every step by hand, it pauses exactly where a human decision matters, and it
          produces a record a reviewer can reconstruct. That is the difference between a playbook that describes work
          and a process that does work, with the human still at the gate.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Write your first process as a graph at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is a process the same as a workflow?', a: 'Close, but with a stricter contract: a Raisy process is a directed graph whose steps are executed by bots and whose external edges carry approval gates, all logged per run.' },
      { q: 'Can a process branch?', a: 'Yes. Branches are just edges; the run follows the graph.' },
      { q: 'What happens at a gate?', a: 'The run pauses, the prepared action is shown, and a human approves, edits, or rejects. The gate decision is logged with the run.' },
      { q: 'Can I run a process without gates?', a: 'Autonomy is earned per bot, not turned off globally. Gates scale down as a bot proves reliable, but the run is always recorded.' },
    ],
    sources:
      'Raisy System Manifest (MR0 review gate before external effects; MR0b process/bot/kernel levels; MR0a every state change logged). Live verification pending a public process API: logged as a setup task before publication.',
  },
  {
    slug: 'a-kernel-is-a-contract',
    title: 'Versioned System Prompts Without Losing History',
    date: '2026-08-06',
    category: 'Engineering',
    series: 'The Raisy Blog',
    audience: { persona: 'BotBuilder', background: 'Professional', depth: 'Applied' },
    image: '/blog/a-kernel-is-a-contract.jpg',
    excerpt:
      'Edit a system prompt and you lose history. Edit a kernel and you create a new immutable version. Why Raisy treats every rule a bot knows as a versioned contract.',
    keywords: ['versioned AI system prompts', 'immutable AI rules', 'kernel versioning', 'AI knowledge management', 'bot kernel classification'],
    content: (
      <>
        <p>
          Edit a system prompt and you lose history. Edit a kernel and you create a new version. That one difference
          decides whether the rules your AI follows can be trusted, reused, and audited, or whether they are just text
          that quietly drifts.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Most AI &quot;knowledge&quot; lives as a prompt string in a file. Change it and the old version is gone: you
          have no record of which version produced yesterday&apos;s output, rolling back means git archaeology, and a
          good rule is not reusable in another bot without copy-pasting. A prompt is a contract written in pencil.
        </p>
        <p>
          The second is discovery. When a company accumulates rules, nobody can answer &quot;which bot already
          knows how to do this&quot;, so teams re-write the same instruction over and over, and the copies drift apart.
          (This is one level of the{' '}
          <Link to="/blog/ai-company-operating-system" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Raisy company operating system
          </Link>
          ; the same immutability is why{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            bot training
          </Link>{' '}
          never breaks the audit trail. Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          A kernel is a unit of knowledge with an identity: a stable key, a version, a dimension classification, and a
          scope. When you edit it, Raisy creates a new version and keeps the old one, so every bot call that used the
          old rule can still be reconstructed against the rule that actually ran. When you teach one bot, every bot that
          shares that kernel improves with it, and the change is a dated event.
        </p>

        <h2>How it works</h2>
        <ul>
          <li><strong>Immutable by default.</strong> Each kernel version is a row with its own content hash; updating creates a new version row, never an overwrite.</li>
          <li><strong>Stable key across versions.</strong> The kernel key never changes, so bots and logs keep referencing the same contract as it improves.</li>
          <li><strong>Classified, not free-text.</strong> A kernel carries BD/KD codes from the dimension indexes, so the system can match a request to the right kernel instead of guessing.</li>
          <li><strong>Scoped to global or private.</strong> A global kernel is shared and reusable; a private kernel belongs to one entity and is not exposed to others.</li>
          <li><strong>Reuse before create.</strong> When a bot needs a shared slot, Raisy reuses an existing active kernel at the right scope before minting a new one: leaner bots, fewer copies.</li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          You edit the rule that governs how your sales bot qualifies leads. Instead of a silent overwrite, you get a
          version: the new kernel, the old kernel, and the date the bot switched. Every past run shows which version
          was in effect. You can promote the new version, roll back to the old one, or share the improved rule with the
          recruiting bot that uses the same slot.
        </p>

        <ChartZoom><KernelVersionChart /></ChartZoom>

        <p>
          The chart above shows why this is not optional. Between March and June 2023, GPT-4, the same model
          name, with no version bump a user could see, dropped from 84.0% to 51.1% accuracy on prime-vs-composite
          classification, from 83.6% to 35.2% on happy-number counting, and from 52.0% to 10.0% on
          directly-executable code generation, while its response rate on sensitive questions fell from 21.0% to 5.0%
          (Chen, Zaharia &amp; Zou, &quot;How Is ChatGPT&apos;s Behavior Changing over Time?&quot;). If the model
          underneath your bot silently changes behavior within three months, the rules you control are the only
          stable ground you have. That is why a behavioral contract must be pinned and versioned, not edited in
          place.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          Rules you can version are rules you can audit, and rules you can audit are rules you can trust. The moment AI
          knowledge stops being throwaway text and becomes versioned contracts, three things change: outputs become
          reconstructible, good rules stop being re-invented, and a company&apos;s accumulated know-how becomes an asset
          instead of a pile of prompts.
        </p>

        <h2>What this post does not answer</h2>
        <p>
          This post describes the kernel model and why versioning matters. It does not publish the dimension-index
          contents, the classifier&apos;s selection rules, or any kernel prompt text, those are internal. It does not
          measure reuse rates or version churn empirically (no public kernel API yet). It does not compare kernel
          versioning against prompt-engineering workflows from other tools. Those are next steps once the kernel API is
          public.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Version your first kernel at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is a kernel the same as a system prompt?', a: 'No. A system prompt is a string; a kernel is a versioned, classified, reusable unit that a bot loads into a slot.' },
      { q: 'What happens to old versions?', a: 'They stay in the trail, immutable, so any past run can be reconstructed against the rule that actually ran.' },
      { q: 'Can two bots share one kernel?', a: 'Yes, when the kernel is global. Teach one bot and every bot sharing that kernel improves, and the change is a dated, reviewable event.' },
      { q: 'Is editing free?', a: 'Editing is always possible. The point is that it is versioned, so improvement never erases the audit trail.' },
    ],
    sources:
      'Chen, Zaharia & Zou, "How Is ChatGPT\u2019s Behavior Changing over Time?", arXiv:2307.09009v3 (https://arxiv.org/abs/2307.09009), published in Harvard Data Science Review 6(2), 2024; code and data: https://github.com/lchen001/LLMDrift. Figures cited are from the v3 revision, the v1 preprint\u2019s 97.6%\u21922.4% prime-vs-composite drop was revised by the authors. Raisy System Manifest (MR0c dimension indexes, MR7b/MR8b reuse-before-create, MR8 versioned-never-overwritten). Internal capabilities referenced by name (kernel classifier, bot constructor). Live verification pending a public kernel API: logged as a setup task before publication.',
  },
  {
    slug: 'every-action-leaves-a-receipt',
    title: 'AI Agent Audit Trail: Every Action Leaves a Receipt',
    date: '2026-08-06',
    category: 'Engineering',
    series: 'The Raisy Blog',
    audience: { persona: 'OpsLead', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/every-action-leaves-a-receipt.jpg',
    excerpt:
      'Every bot call and process run writes an immutable, dated log row. Here is how Raisy turns AI audit trails into something you can point to.',
    keywords: ['AI agent audit trail', 'bot call logs', 'AI governance', 'auditable AI agents', 'ISO 9001 AI'],
    content: (
      <>
        <p>
          An AI that works for your company but does not log its work is not an employee. It is a risk. When a machine
          acts on your behalf, the only thing that makes that safe is the ability to reconstruct what it did, when, and
          why. Raisy treats that trail as a product requirement, not a compliance chore: every bot call, every process
          run, every approval, and every promotion writes an immutable, dated log row you can point to.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Two weaknesses come up again and again with AI assistants. The first is opacity: a model produces an
          answer, and nobody can say which rule, which input, or which run produced it. The second is drift: you have no way to
          tell whether a bot you approved three weeks ago is still doing what you approved, because nothing records the
          boundary it crossed.
        </p>
        <p>
          The standard answer in the market is to show a chat transcript. A transcript is not an audit trail. It shows
          what a person typed and what a model replied; it does not show the process that ran, the gate that was passed,
          or the version of the rules that applied. (This is the same standard we hold the{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            bot lifecycle
          </Link>{' '}
          to, and the reason every run in{' '}
          <Link to="/blog/a-process-is-a-graph-with-approval-gates" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            a process
          </Link>{' '}
          can be reconstructed. Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>Raisy logs the system, not the chat. Every execution is an event with a type, a timestamp, an actor, and the version of the rules that governed it:</p>
        <ul>
          <li><strong>A bot call</strong> records which bot ran, which kernels were loaded, what it prepared, and what mode it ran in.</li>
          <li><strong>A process run</strong> records which graph of steps executed, in what order, and where it paused.</li>
          <li><strong>An approval</strong> records who approved, edited, or rejected a prepared action.</li>
          <li><strong>A promotion</strong> records when a bot moved from MANUAL to SEMI_AUTO to FULL_AUTO, and why.</li>
        </ul>
        <p>Each row is appended, not overwritten. A kernel that changes creates a new version; the old version stays in the trail. Nothing a machine did for you silently disappears.</p>

        <h2>What the user actually sees</h2>
        <p>
          Open a bot&apos;s record and you see its life as a dated event stream: hired, called, gated, approved,
          edited, promoted. Each row carries a timestamp, the actor (bot or human), the action taken, its outcome,
          and, where a gate was involved: the approver. Each event expands to its detail: the input, the prepared
          output, the gate that passed. You can replay what happened without trusting a summary.
        </p>

        <ChartZoom><AuditTrailChart /></ChartZoom>

        <p>
          The chart above is not a product screenshot; it is the market context. Documented AI incidents nearly
          tripled in two years: 123 in 2023, 233 in 2024, 362 in 2025, per the AI Incident Database figures reported
          in Stanford HAI&apos;s AI Index Reports. Every one of those incidents is an event where someone needed a
          receipt and either had one or did not. Regulation is moving the same way: the EU AI Act (Regulation (EU)
          2024/1689) requires high-risk AI systems to automatically record event logs over the system&apos;s lifetime
          (Article 12), with the regulation applying in full from 2 August 2026 and the Article 6(1) obligations
          following on 2 August 2027. Automatic logging is becoming law, not preference.
        </p>

        <h2>Why this matters beyond convenience</h2>
        <p>
          The promise is auditability by design, not as an afterthought, the same standard an ISO 9001 process would
          demand. When a machine acts on your behalf, the trail is what turns it from a tool you gamble on into a
          workforce you can stand behind. It is also what makes the human gate meaningful: a reviewer with no way to
          reconstruct a run has no basis for approving it.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Watch your first run log itself at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Does this apply to my strategy or my department?', a: 'Yes. The log is per entity: your company owns its rows, its bots, its process runs.' },
      { q: 'Can a bot act without a receipt?', a: 'No. An external action always passes a gate, and every gate writes a row. Autonomy changes how often you review, never whether the action is recorded.' },
      { q: 'Is the log editable?', a: 'Rows are append-only. A correction is a new row that references the original; the original stays in the trail.' },
      { q: 'What happens when I promote a bot?', a: 'The promotion is logged with its mode and timestamp, so you can always tell when and why a bot\u2019s latitude changed.' },
    ],
    sources:
      'Stanford HAI AI Index Report 2024, chapter 3 (https://hai.stanford.edu/assets/files/hai_ai-index-report-2024_chapter3.pdf); Stanford HAI AI Index Report 2025, chapter 3 (https://hai.stanford.edu/assets/files/hai_ai_index_report_2025.pdf); Stanford HAI AI Index Report 2026, chapter 3 (https://hai.stanford.edu/assets/files/ai_index_report_2026.pdf): AI Incident Database counts of documented AI incidents per year (123 in 2023, 233 in 2024, 362 in 2025). Regulation (EU) 2024/1689 (EU AI Act), Articles 12 and 113 (https://eur-lex.europa.eu/eli/reg/2024/1689/oj). Raisy System Manifest (MR0: review gate before external effects; MR0a: every action, output, and state change is logged). Execution rows are reproducible from Raisy\u2019s immutable audit log of bot and process calls once the public execution-log API ships: pending and logged as a setup task before publication.',
  },
  {
    slug: 'ai-company-operating-system',
    title: 'An AI Company Operating System That Logs Everything',
    date: '2026-08-03',
    category: 'Vision',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Beginner', depth: 'Overview' },
    image: '/blog/ai-company-operating-system.jpg',
    excerpt:
      'Paste a URL and get a running AI company: bots for every department, processes for every function, every action logged. Raisy is not another tool.',
    keywords: ['AI company operating system', 'autonomous company OS', 'AI workforce platform', 'AI departments'],
    content: (
      <>
        <p>
          You are a founder with a growing company and a shrinking list of things you can do yourself. You have tried
          Apollo for outreach, a CRM to track it, a scheduling tool, a support inbox, and a spreadsheet that ties it
          all together. None of them run a company. They are tools that a person still has to operate.
        </p>
        <p>
          Raisy is not a tool. It is an AI company operating system: paste a URL and an entire department shows up,
          already running, with every action logged for you to audit.
        </p>

        <h2>What we set out to change</h2>
        <p>
          The cost is not absent software. The cost is that every function of a young company: fundraising, sales,
          recruiting, support, is a separate process with a separate tool and a separate human running it. Buy the
          tool and you still have to hire the operator. That is why &quot;a better outreach tool&quot; is the wrong
          category; the comparison that matters is the cost of the work, not the price of another tool.
        </p>
        <p>
          The second weakness is trust. Give an AI a job and there is no record of what it did, when, or why. A black box
          that produces output is not an employee; it is a risk.
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          Raisy treats a company as a system you can build, run, and audit, not as a folder of apps. Every organized
          actor is an <strong>entity</strong>. Every repeatable method is a <strong>process</strong>. Every actor that
          executes a step is a <strong>bot</strong>. Every rule a bot knows is a <strong>kernel</strong>: versioned,
          immutable, and traceable.
        </p>
        <p>
          Paste your homepage URL and Raisy builds your entity profile, proposes bots for every relevant department,
          and deploys the processes that make them useful. The company does not get a suite of tools; it gets a
          workforce. (See how that workforce is priced in{' '}
          <Link to="/blog/ai-workforce-roi" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            our ROI post
          </Link>
          , and how individual bots earn autonomy in{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            our lifecycle post
          </Link>
          . More on the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>How it works (the technical layer)</h2>
        <ul>
          <li><strong>Entity → Process → Bot → Kernel</strong>, top-down. You start with who acts, then what they do repeatedly, then who executes each step, then what that actor knows.</li>
          <li><strong>Bots have a lifecycle.</strong> Hired, then probation (you review every output), then promoted through SEMI_AUTO to FULL_AUTO as confidence grows. You set the trust level; the system earns autonomy.</li>
          <li><strong>Every run is logged.</strong> Each bot call and each process run writes an immutable log row. If an action would cross to the outside world, the run pauses for your review. AI prepares; you gate.</li>
          <li><strong>Kernels are versioned.</strong> Change a rule and a new version is created; the old one stays for the audit trail. Your bots improve without losing history.</li>
        </ul>

        <h2>What the user actually sees</h2>
        <p>
          You paste yourcompany.com into Raisy. Minutes later you have a company profile, a list of proposed bot roles
          per department, and deployed processes behind them. You review the first outputs, promote the bots that earn
          it, and watch the log fill with every action: searchable, dated, and attributable.
        </p>

        <BlogImage
          src="/blog/ai-company-operating-system-flow.png"
          alt="From URL to a running AI company: paste a URL, bots proposed, processes deployed"
          caption="Figure 1. The three-step flow: paste a URL, bots are proposed for every department, processes deploy with human review."
        />

        <ChartZoom><RaisyStackChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          The promise is auditability by design, not as an afterthought. When a machine acts on your behalf, the only
          thing that makes that safe is the ability to reconstruct what it did and why. Raisy was built so every
          decision has a trail, the same standard an ISO 9001 process would demand. That is what turns an AI from a
          tool you gamble on into a workforce you can stand behind.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Paste your company URL at raisy.team and watch your first department arrive
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is Raisy another project-management tool?', a: 'No. A tool helps a person do work; Raisy runs organized actors (entities), their repeatable methods (processes), the executors (bots), and the rules they know (kernels), and logs all of it.' },
      { q: 'Do I have to build the system myself?', a: 'No. Paste a URL and Raisy proposes your entity profile, departments, bots, and processes; you review what it prepares.' },
      { q: 'How does this relate to the ROI post?', a: 'The operating system is the architecture; the ROI post prices what a department built on it costs. The lifecycle post explains how individual bots earn autonomy inside it.' },
    ],
    sources:
      'Raisy System Manifest (RAISY_01_MANIFEST), Raisy Core Value (RAISY_CORE_VALUE). Live verification of bot/process logs pending a public API run: logged as a setup task before publication.',
  },
  {
    slug: 'ai-workforce-roi',
    title: 'A 20-Role AI Department for $24K a Year',
    date: '2026-08-03',
    category: 'Value',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/ai-workforce-roi.jpg',
    excerpt:
      'A full department of bot workload for $24,000 a year, with people at every decision gate. The Raisy ROI math, with the numbers.',
    keywords: ['AI workforce platform', 'human-directed AI agents', 'ROI of AI agents', 'AI company cost'],
    content: (
      <>
        <p>
          Staffing a single junior marketing role costs about $60,000 a year. Running an entire department&apos;s{' '}
          <em>workload</em> on Raisy costs around $24,000 a year: fundraising research, sales outreach,
          recruiting screens, support triage. This post prices workloads, not people: the point of the math is that a young
          company can afford both the bots and, more importantly, the humans who direct them. The number should not be
          a vibe; it should be a spreadsheet.
        </p>

        <h2>What we set out to change</h2>
        <p>
          The real budget a founder protects is the hiring budget. Software budgets are small and forgiving; headcount
          is large and permanent. Every time a company adds a function: outreach, investor research, recruiting,
          support. It faces the same choice: hire a person for the whole stack of tasks, or pay an agency. Both are
          expensive, and neither scales down when the workload does. (This is the{' '}
          <Link to="/blog/ai-company-operating-system" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Raisy vision
          </Link>{' '}
          in numbers; read about how bots earn trust in{' '}
          <Link to="/blog/a-bot-with-a-career-path" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            our lifecycle post
          </Link>
          , or browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          Raisy prices the toil, not the people. Instead of asking &quot;what does this <em>person</em> cost,&quot;
          the question becomes &quot;which of this function&apos;s tasks should a bot prepare, so a person can
          decide.&quot; Bots take the drafting, sourcing, and tracking; humans keep the judgment, the relationships,
          and the final call. The bots are employees with a career path: reviewed, promoted, and audited, not
          one-off automations, and never a replacement for the people who run them.
        </p>

        <h2>The math</h2>
        <ul>
          <li><strong>Fundraising researcher + pitch writer + tracker:</strong> $220,000/yr fully staffed → $3,600/yr Raisy.</li>
          <li><strong>Sales BDR + email copywriter + LinkedIn SDR:</strong> $215,000/yr → $4,800/yr.</li>
          <li><strong>Recruiting sourcer + screener + scheduler:</strong> $105,000/yr → $3,600/yr.</li>
          <li><strong>Support agent + auto-responder:</strong> $80,000/yr → $2,400/yr.</li>
          <li><strong>IR manager + board pack assembler:</strong> $170,000/yr → $2,400/yr.</li>
        </ul>
        <BottomLine>
          A 20-role AI department costs <strong>$1,185,000/yr</strong> fully staffed vs <strong>$24,000/yr</strong> on
          Raisy: a 98% reduction in workload cost, with people kept at every decision gate, not replaced by it.
        </BottomLine>

        <p>
          Every role is a set of kernels (rules the bot follows) deployed inside a process, with human review at the
          boundary. When you grow, you deploy another bot; when a function slows, you pause it, and the people you
          hire spend their time on judgment, not prep work.
        </p>
        <p>
          The numbers behind this table are downloadable:{' '}
          <a href="/blog/raisy-20-role-cost-model.csv" download className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            get the 20-role cost model as a CSV
          </a>
          .
        </p>

        <h2>What the user actually sees</h2>
        <p>
          You open Raisy and see your departments as a roster. Each bot shows its role, its current approval mode, its
          confidence, and its audit log. Promote a bot to FULL_AUTO and it keeps working while you watch the trail.
          Pause a bot and the cost stops with it.
        </p>

        <ChartZoom><CostGapChart /></ChartZoom>

        <h2>Why this matters beyond convenience</h2>
        <p>
          This is not a discount on software; it is a change in what a company can afford to attempt. Fully staffing
          this department costs $1.1M a year, out of reach for most founders. The bot workload is not. When the cost
          of running a function drops by 98%, the constraint stops being money and starts being judgment, and judgment
          is human work. That is the point of Raisy: bots do the toil; people make the decisions. Everything
          comes prepared, the only thing left for a human is the call that matters.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            See the full cost model at raisy.team and price your own department
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Are you pricing people or workloads?', a: 'Workloads. Each line is the cost of staffing that function\u2019s tasks with full-time hires, compared to the same tasks prepared by bots with human review at the gate. People are the decision-makers, never the thing being priced.' },
      { q: 'Where do the numbers come from?', a: 'Raisy\u2019s value model (committed value data) maps bot roles to full-staffing cost equivalents; the table is reproducible from that model.' },
      { q: 'Do I need all 20 roles?', a: 'No. Deploy one bot or a full department; the model scales down and up, and you pause a bot\u2019s cost when a function slows.' },
      { q: 'What do the humans do?', a: 'They review every external action and make the calls that matter: approvals, edits, and judgment. The bots prepare; the people decide.' },
    ],
    sources:
      'Raisy\u2019s internal workforce value model (20 bot roles vs full-staffing cost equivalents). Numbers transcribed from the committed value data; re-run the model to reproduce.',
  },
  {
    slug: 'a-bot-with-a-career-path',
    title: 'AI Agent Lifecycle: Hired, Promoted, Fired',
    date: '2026-08-03',
    category: 'Engineering',
    series: 'The Raisy Blog',
    audience: { persona: 'OpsLead', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/a-bot-with-a-career-path.jpg',
    excerpt:
      'Hired, reviewed, promoted, fired. Raisy bots have a lifecycle, a confidence score, and an audit trail, so you scale trust the same way you scale work.',
    keywords: ['AI agent lifecycle', 'bot workforce management', 'bot promotion workflow', 'trusted AI agents'],
    content: (
      <>
        <p>
          Most AI agents are fire-and-forget: you prompt them, they answer, and you never see them again. Raisy treats
          bots differently: as employees with a lifecycle. Hired into probation, reviewed on every output, promoted as
          confidence grows, and trained from your feedback. The difference is not a feature list. It is trust you can
          scale.
        </p>

        <h2>What we set out to change</h2>
        <p>
          Two things make people reluctant to hand work to AI: unreliability and opacity. An agent that acts without
          oversight is a liability, and an agent with no audit trail is a black box. The standard answer in the market is
          to keep everything in manual mode forever, which defeats the point of automation. (This lifecycle is one
          piece of the{' '}
          <Link to="/blog/ai-company-operating-system" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            Raisy company operating system
          </Link>
          ; the{' '}
          <Link to="/blog/ai-workforce-roi" className="text-cyan-700 dark:text-cyan-400 underline underline-offset-2">
            cost model
          </Link>{' '}
          explains what a promoted workforce is worth. Browse the <BlogHubLink>blog</BlogHubLink>.)
        </p>

        <h2>The idea / how Raisy fixes it</h2>
        <p>
          The fix is a lifecycle with a review gate where it matters. A bot starts in <strong>MANUAL</strong> mode: it
          prepares, you review every run, and nothing reaches the outside world without you. As its confidence score
          climbs, you promote it through <strong>SEMI_AUTO</strong>: AI prepares, you approve before it acts externally
         , and then to <strong>FULL_AUTO</strong>, where it runs and you audit anytime. Autonomy is earned, not assumed.
        </p>

        <h2>How the lifecycle works</h2>
        <ul>
          <li><strong>Hired:</strong> a set of kernels is created for the role.</li>
          <li><strong>Probation:</strong> MANUAL approval mode, every output is reviewable before it acts.</li>
          <li><strong>Reviewed:</strong> a confidence score is tracked per bot, per run.</li>
          <li><strong>Promoted:</strong> you move it MANUAL → SEMI_AUTO → FULL_AUTO. Your call, not the machine&apos;s.</li>
          <li><strong>Trained:</strong> feedback patches the bot&apos;s knowledge: teach one bot, and every bot sharing that kernel improves.</li>
          <li><strong>Fired:</strong> archived with its full audit trail.</li>
        </ul>
        <p>
          Every step of that lifecycle writes a log row. Promotion, training, and firing are events you can point to.
        </p>

        <h2>What the user actually sees</h2>
        <p>
          Your roster shows each bot with its role, its current approval mode, and its confidence score. You approve a
          run, the score goes up, you promote the bot, and it starts handling more on its own, while the audit log
          keeps growing underneath. You never have to choose between &quot;unreviewed AI&quot; and &quot;AI that never
          does anything.&quot;
        </p>

        <ChartZoom><BotCareerPathChart /></ChartZoom>

        <BlogImage
          src="/blog/a-bot-autonomy-earned.png"
          alt="Autonomy is earned: autonomy rises and human review falls as a bot progresses through its lifecycle"
          caption="Figure 2. Autonomy is earned, not assumed: autonomy rises and review need falls as confidence grows."
        />

        <h2>Why this matters beyond convenience</h2>
        <p>
          Trust is not binary. A new hire does not get the keys on day one; a reliable one earns more latitude over
          time. If AI is going to run real parts of a company, it should earn trust the same way: measurably,
          reviewably, and reversibly. That is the difference between an agent you experiment with and a workforce you
          rely on.
        </p>

        <p className="mt-8">
          <a href="https://raisy.team" className="inline-block bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg">
            Deploy your first bot and watch it earn its promotion at raisy.team
          </a>
        </p>
      </>
    ),
    faq: [
      { q: 'Is a bot ever fully unsupervised?', a: 'No. FULL_AUTO means the bot runs and you audit anytime; every action is still logged, and you can demote it at any time.' },
      { q: 'What drives the confidence score?', a: 'Your approvals and edits across runs. The bot earns latitude by what you actually approve: not by asking for it.' },
      { q: 'Can I skip straight to FULL_AUTO?', a: 'You set the trust level, so technically yes, but the design intent is that autonomy is earned, because reviewed bots are bots you can trust.' },
      { q: 'What happens when a bot is trained?', a: 'Feedback patches its kernel set; every bot sharing a kernel improves with it, and the change is a dated, reviewable event.' },
    ],
    sources:
      'Raisy System Manifest (MR0 / MR0b: manual → semi-auto → full-auto), project068 eval-artifact framework (confidence score), project073 Rowi enterprise readiness. Log rows are reproducible from Raisy\u2019s immutable audit log of bot and process calls once the public execution-log API ships: pending and logged as a setup task before publication.',
  },
  // blog30 - the funnel, step by step. The eight steps are the PO's own list and the order is the
  // point. Four of them are gates nobody has measured, so they are drawn as gates and never as
  // numbers; the value the pre-filter adds is shown OUTSIDE the sankey, because a sankey draws one run.
  {
    slug: 'the-funnel-of-your-raise',
    title: 'The Fundraising Funnel: Where 1,320 Reachouts Go',
    date: '2026-10-09',
    category: 'Research',
    series: 'The Raisy Blog',
    audience: { persona: 'PreSeedFounder', background: 'Intermediate', depth: 'Applied' },
    image: '/blog/the-funnel-of-your-raise.jpg',
    excerpt:
      'Eight steps stand between an investor list and a signed round, and four of them have never been measured. The figure walks all eight, and shows separately what a fit-ranked list is worth: the reply band moves from 2-5% to 3-10%.',
    keywords: [
      'fundraising funnel',
      'investor outreach conversion rate',
      'sales funnel sankey',
      'reachouts to meetings',
      'b2b fundraising benchmarks',
    ],
    content: (
      <>
        <p>
          <em>Our conversion is 0.9 percent.</em> That number is true and useless: it is the product
          of three separate losses — replies that never come, replies that never become meetings, and
          meetings that never close — and it names none of them.
        </p>
        <p>
          The funnel is not one step. It is eight, and the order matters: a potential investor list,
          filtered by geography, scored for fit, sent to, answered, booked into a meeting, carried
          through diligence, and finally closed. Each step removes some of what reached it.
        </p>

        <ChartZoom>
          <FunnelSankeyChart />
        </ChartZoom>

        <h2>Four of the eight steps have never been measured</h2>
        <p>
          The list, the geography filter, the fit threshold and the diligence stage are all real parts
          of the process, and none of them has a measured drop-off rate. So the figure draws them as
          <em>gates</em> and not as numbers: a filter rate nobody has measured cannot honestly set the
          width of a band, and a band drawn anyway would be a fabrication wearing a measurement's
          clothes.
        </p>
        <p>
          Diligence is a little different from the other three. It is a gate, but it is also a stage
          every meeting passes through, so the flow shows it: meetings go in, and the split into
          funded and not-funded happens after it. What is unmeasured is diligence&rsquo;s <em>own</em>
          share of the loss — the 35% win rate spans the whole distance from meeting to close, so
          nothing separates the conversations that died in diligence from the ones that died for some
          other reason.
        </p>
        <p>
          What <em>is</em> measured is what happens after the gates: how many sent reachouts come
          back, how many of those become meetings, and how many of those close.
        </p>

        <h2>What the pre-filter is worth, shown outside the chart</h2>
        <p>
          A Sankey draws one run, so it cannot show a difference between two of them. That comparison
          sits above the flow instead, and it is the whole of the claim: cold outreach replies run
          <strong>2&ndash;5%</strong> to an unfiltered list, and <strong>3&ndash;10%</strong> with a
          fit-ranked list and an email written for each investor. Same volume, same seller, different
          band.
        </p>
        <p>
          That is the part worth being careful about. The uplift is not a productivity trick and it is
          not a better email; it is <em>fewer, better-chosen recipients</em>. The gate earns its keep
          before a single message is written.
        </p>
        <h2>The part that argues against us</h2>
        <p>
          The two bands <em>overlap</em>. A fit-ranked list replies on 3&ndash;10% of reachouts; an
          unfiltered one on 2&ndash;5%. At the conservative ends of those ranges &mdash; 3% against 5%
          &mdash; the unfiltered list wins, so the honest claim is not <strong>1.9x</strong> but a
          range: <strong>0.6x to 5.0x</strong>, with 1.9x at the midpoints. We are publishing that
          because a figure showing two ranges and one confident multiplier would be showing you the
          multiplier and hiding the overlap, and the overlap is the whole uncertainty.
        </p>
        <p>
          The bars are drawn so you can check it for yourself. They run from zero. The solid part is the
          bottom of the published band; the lighter part above it is how much further that band reaches.
          Both entities&rsquo; ranges are therefore visible against each other on the same scale, which
          is the only version of this comparison a reader can audit.
        </p>
        <p>
          So what does the pre-filter actually buy? A better <em>expected</em> reply rate, not a
          guaranteed one. That is a weaker claim than the marketing version, and it is the one the
          evidence carries.
        </p>

        <h2>Two chains, 3.85x apart</h2>
        <p>
          There is one thing this figure cannot resolve, and it belongs in the open. Our own model
          runs the same 1,320 reachouts through a chain with <em>no reply stage at all</em> — a 10%
          rate straight from send to meeting — and reaches <strong>132</strong> meetings where the
          published ladder reaches <strong>34.3</strong>. Nothing has reconciled the two. Until
          something does, the honest figure is the one drawn on the band the evidence carries, with
          the disagreement stated rather than averaged away.
        </p>

        <h2>Why we are showing the gates we cannot measure</h2>
        <p>
          Because a benchmark you cannot re-derive is a number you have to trust, and trust is the
          thing a funnel should not require. Marking four steps as unmeasured is less impressive than
          printing five confident percentages, and it is the only version of this figure that a reader
          can check.
        </p>
      </>
    ),
    faq: [
      { q: 'What is a good conversion rate for investor outreach?', a: 'Published benchmarks sit in wide bands, and the useful question is not the headline rate but the step: the reply band moves from 2-5% to 3-10% when the list is fit-ranked, and the loss after the meeting is a different problem again. This figure shows the steps separately.' },
      { q: 'Why are four of the eight steps marked unmeasured?', a: 'The investor list, the geography filter, the fit >80% threshold and the diligence stage are all real parts of the process, and none of them has a measured drop-off rate. Drawing them as gates rather than inventing a width is the honest option.' },
      { q: 'Is the reply rate measured?', a: 'No. Our own published post on outreach costs states we do not yet have a measured reply rate, and both funnel rates in the model are marked as the plan\u2019s own choice rather than a market observation.' },
    ],
    sources:
      'blog16 \u2014 what investor outreach actually costs (the 2-5% and 3-10% reply bands, the 40% reply-to-meeting rate, and the 6.5% midpoint) and FM-0013 KERNEL (sales_win_rate 35%, per-lane reachout volumes). Every number in the figure is read from the committed data file; the component computes only the per-step counts.',
  },
];
